/**
 * Family Tree detail: header, member list, CSS-based generational tree view,
 * events tab. Admins can add members, link relationships, create events.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { createPortal } from "react-dom";
import { Link, useParams, useNavigate } from "react-router-dom";
import { http, resolveImage } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import QRInvitePoster from "@/components/QRInvitePoster";
import AiSmartAddModal from "@/components/AiSmartAddModal";
import FamilyMigrationMap from "@/components/FamilyMigrationMap";
import { EVENT_CATEGORIES, categoriesWithEvents, getEventMeta } from "@/lib/eventCategories";
import { Plus, Users, Calendar, Settings, Flower2, ExternalLink, Trash2, Link2, Image as ImageIcon, MessageCircle, Send, X, Share2, Download, Upload, MessagesSquare, Copy, Clock, ShieldCheck, UserPlus, Pencil, ChevronDown, ChevronUp, UserCircle2, Loader2, BookOpen, MapPin, FileText, Archive, Maximize2, Minimize2, MoreHorizontal, Mail, Home, Sparkles, Map as MapIcon } from "lucide-react";
import TreeMinimap from "@/components/family-tree/TreeMinimap";
import TreeMiniCard from "@/components/family-tree/TreeMiniCard";
import QuickEditDrawer from "@/components/family-tree/QuickEditDrawer";
import FamilyTreePageSearch from "@/components/family-tree/FamilyTreePageSearch";
import {
  HERITAGE_PALETTE,
  TREE_CARD_W,
  TREE_CARD_H,
  COUPLE_GAP,
  SIBLING_GAP,
  GEN_GAP,
  ROW_H,
  H_PAD,
  V_PAD,
} from "@/components/family-tree/treeConstants";
import { API } from "@/lib/api";
import FamilyChat from "@/components/FamilyChat";
import TreeBanner from "@/components/TreeBanner";
import FamilyDiscoveries from "@/components/family/FamilyDiscoveries";
import MemberContributeModal from "@/components/family/MemberContributeModal";
import WishComposerModal from "@/components/family/WishComposerModal";
import FamilyMemberContactModal from "@/components/FamilyMemberContactModal";
import TranslatableInput from "@/components/TranslatableInput";
import AIImproveButton from "@/components/AIImproveButton";
import AncestorAvatar from "@/components/AncestorAvatar";
import InitialsAvatar from "@/components/InitialsAvatar";
import FamilyUpcoming from "@/components/family/FamilyUpcoming";
import CopyLinkIcon from "@/components/CopyLinkIcon";
import VisibilityToggle from "@/components/VisibilityToggle";
import SplinkCard from "@/components/SplinkCard";
import TreeCorrectionsPanel from "@/components/TreeCorrectionsPanel";
import InviteManagerPanel from "@/components/InviteManagerPanel";
import { pickI18n } from "@/lib/i18n";
import { useLocality } from "@/contexts/LocalityContext";

export default function FamilyTreeDetail() {
  const { treeId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tree, setTree] = useState(null);
  const [members, setMembers] = useState([]);
  const [rels, setRels] = useState([]);
  const [events, setEvents] = useState([]);
  const [tab, setTab] = useState(() => {
    try {
      const t = new URLSearchParams(window.location.search).get("tab");
      return ["tree", "members", "timeline", "events", "albums", "documents", "archive", "chat", "invite", "access"].includes(t) ? t : "tree";
    } catch { return "tree"; }
  });
  const [memberModal, setMemberModal] = useState(false);
  const [aiSmartAddOpen, setAiSmartAddOpen] = useState(false);
  const [migrationMapOpen, setMigrationMapOpen] = useState(false);
  const [bulkInviteOpen, setBulkInviteOpen] = useState(false);
  const [qrPosterOpen, setQrPosterOpen] = useState(false);
  const [relModal, setRelModal] = useState(false);
  const [eventModal, setEventModal] = useState(false);
  const [shareModal, setShareModal] = useState(false);
  const [gedcomModal, setGedcomModal] = useState(false);
  const [editTreeModal, setEditTreeModal] = useState(false);
  const [error, setError] = useState(null);
  // Public "share" card used when the current user can't view the tree
  // (private/invite tree + non-member). Fetched lazily on error.
  const [publicCard, setPublicCard] = useState(null);
  // "Where am I?" one-tap action — scrolls to and highlights the current
  // user's member card. Triggered by ?whereami=1 or the floating button.
  const [whereAmIBusy, setWhereAmIBusy] = useState(false);

  const myMember = useMemo(() => {
    if (!user || !members || !members.length) return null;
    return members.find((m) =>
      m.claimed_by_user_id === user.id ||
      m.linked_user_id === user.id ||
      (m.user_ids || []).includes(user.id)
    ) || null;
  }, [user, members]);

  const goToMyself = useCallback(() => {
    if (!myMember) {
      // Not linked to a member — take the user to the tree join / claim flow.
      toast.info("You're not linked to a family member yet. Ask an admin to add you or claim a profile.");
      return;
    }
    setWhereAmIBusy(true);
    setTab("tree");
    // Wait a tick so the tree tab is rendered before we hunt for the card.
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="family-member-${myMember.id}"]`);
      if (!el) { setWhereAmIBusy(false); return; }
      el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
      // Amber highlight ring that pulses for 3 seconds.
      const prevBox = el.style.boxShadow;
      const prevTx = el.style.transform;
      el.style.boxShadow = "0 0 0 4px rgba(194,65,12,0.55), 0 12px 32px -8px rgba(194,65,12,0.45)";
      el.style.transform = "scale(1.04)";
      el.style.transition = "box-shadow 350ms ease, transform 350ms ease";
      setTimeout(() => {
        el.style.boxShadow = prevBox;
        el.style.transform = prevTx;
        setWhereAmIBusy(false);
      }, 3000);
    }, 250);
  }, [myMember]);

  // Honour ?whereami=1 deep-link from the mobile shell.
  useEffect(() => {
    if (!members || !members.length) return;
    try {
      const q = new URLSearchParams(window.location.search);
      if (q.get("whereami") === "1") {
        goToMyself();
        // Strip the query so refresh doesn't retrigger.
        q.delete("whereami");
        const qs = q.toString();
        window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members, myMember]);

  // Whether the current user can administer this tree. Super admins
  // (bijujoe / tomvell / sajijoseph — anyone with `platform_role` OR
  // `is_super_admin` OR `is_admin`) always qualify, even on trees where
  // they aren't explicitly in `admin_ids`. Without this, the "Add
  // member" / "Invite family" / "Edit tree" buttons don't render at
  // all — the button IS the action, so a missing button looks like
  // "clicking does nothing" from the user's side (Sep 2026 report).
  const isPlatformSuperAdmin = !!(
    user && (
      user.is_admin
      || user.is_super_admin
      || ["admin", "super_admin", "editor", "organization_admin"].includes((user.platform_role || "").toLowerCase())
    )
  );
  const isAdmin = tree && user && (
    tree.owner_id === user.id
    || (tree.admin_ids || []).includes(user.id)
    || isPlatformSuperAdmin
  );

  const load = async () => {
    try {
      const t = await http.get(`/family-trees/${treeId}`);
      // Follow the seed-set redirect for soft-deleted legacy trees.
      if (t.data?.deleted && t.data?.redirect_to_slug && t.data.redirect_to_slug !== treeId) {
        navigate(`/family/${t.data.redirect_to_slug}`, { replace: true });
        return;
      }
      setTree(t.data);
      const m = await http.get(`/family-trees/${t.data.id}/members`);
      setMembers(m.data.items);
      const r = await http.get(`/family-trees/${t.data.id}/relationships`);
      setRels(r.data.items);
      const e = await http.get(`/family-trees/${t.data.id}/events`);
      setEvents(e.data.items);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Cannot load tree";
      setError(detail);
      // If it was a 403 (invite/private tree, non-member), try the public
      // /share endpoint so we can still show a card + a "Request Access"
      // tab. 404s mean the tree really doesn't exist.
      if (err?.response?.status === 403) {
        try {
          const s = await http.get(`/family-trees/${treeId}/share`);
          setPublicCard(s.data);
        } catch { /* private trees return 404 on /share too */ }
      }
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [treeId]);

  if (error) {
    // Prefer the public /share card when available (invite trees). If the
    // tree is fully private OR truly missing, fall back to the simple
    // message.
    if (publicCard) {
      return <RequestAccessLanding card={publicCard} user={user} />;
    }
    return <div className="max-w-3xl mx-auto py-16 px-6 text-center">
      <h2 className="font-display text-2xl">{error}</h2>
    </div>;
  }
  if (!tree) return <div className="max-w-3xl mx-auto py-16 px-6 text-center text-sm">Loading tree…</div>;

  // Per-tree canonical meta so search engines see a unique title, OG
  // card and JSON-LD for each family — the audit flagged trees
  // inheriting SocialPulse branding. Only public trees get indexed;
  // private/invite-only carry noindex.
  const _canonicalTreeUrl = typeof window !== "undefined"
    ? `${window.location.origin}/family/${tree.slug || tree.id}`
    : `https://indianfamilytree.com/family/${tree.slug || tree.id}`;
  const _treeTitle = `${tree.name} — Family Tree · IndianFamilyTree`;
  const _treeDesc = (tree.description || `${tree.name} — a public family tree with ${tree.member_count || 0} members${tree.origin_location ? ` rooted in ${tree.origin_location}` : ""}. Explore branches, memories and heritage on IndianFamilyTree.`).slice(0, 300);
  const _treeIndexable = (tree.visibility || "public") === "public";
  const _treeOgImage = tree.cover_photo || tree.crest_url || undefined;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10" data-testid="family-tree-page">
      <Helmet>
        <title>{_treeTitle}</title>
        <meta name="description" content={_treeDesc} />
        <link rel="canonical" href={_canonicalTreeUrl} />
        {!_treeIndexable && <meta name="robots" content="noindex,follow" />}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="IndianFamilyTree" />
        <meta property="og:title" content={_treeTitle} />
        <meta property="og:description" content={_treeDesc} />
        <meta property="og:url" content={_canonicalTreeUrl} />
        {_treeOgImage && <meta property="og:image" content={_treeOgImage} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={_treeTitle} />
        <meta name="twitter:description" content={_treeDesc} />
        {_treeOgImage && <meta name="twitter:image" content={_treeOgImage} />}
        {_treeIndexable && (
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": tree.name,
            "description": _treeDesc,
            "url": _canonicalTreeUrl,
            "inLanguage": "en-IN",
            "about": { "@type": "Family", "name": tree.name },
          })}</script>
        )}
        {_treeIndexable && (
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home",         "item": typeof window !== "undefined" ? window.location.origin + "/" : "/" },
              { "@type": "ListItem", "position": 2, "name": "Family Trees", "item": (typeof window !== "undefined" ? window.location.origin : "") + "/family" },
              { "@type": "ListItem", "position": 3, "name": tree.name,      "item": _canonicalTreeUrl },
            ],
          })}</script>
        )}
      </Helmet>
      <TreeBanner tree={tree} isAdmin={!!isAdmin} onChanged={load} />
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <Link to="/family" className="text-xs underline" style={{ color: "var(--wofy-primary)" }}>← Back to family trees</Link>
          <h1 className="font-display text-4xl md:text-5xl leading-tight mt-2">{tree.name}</h1>
          <div className="mt-2 text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
            {tree.country || "Global"} · {tree.member_count} member{tree.member_count === 1 ? "" : "s"} · {tree.visibility}
          </div>
          {tree.description && <p className="mt-3 text-sm max-w-2xl" style={{ color: "var(--wofy-text-soft)" }}>{tree.description}</p>}
          <VisibilityToggle
            kind="family-tree"
            currentValue={tree.visibility || "public"}
            canManage={!!isAdmin}
            isSignedIn={!!user}
            targetId={tree.id}
            onSaved={(nv) => setTree((prev) => prev ? { ...prev, visibility: nv } : prev)}
          />
        </div>
        {isAdmin && (
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setEditTreeModal(true)} data-testid="ft-edit-tree" className="wofy-btn-ghost">
              <Pencil className="w-4 h-4" /> Edit tree
            </button>
            <button onClick={() => setMemberModal(true)} data-testid="ft-add-member" className="wofy-btn-primary">
              <Plus className="w-4 h-4" /> Add member
            </button>
            <button onClick={() => setAiSmartAddOpen(true)} data-testid="ft-ai-smart-add" className="wofy-btn-ghost" title="Describe a branch in plain English and let AI draft it">
              <Sparkles className="w-4 h-4" /> AI Smart Add
            </button>
            <button onClick={() => setMigrationMapOpen(true)} data-testid="ft-migration-map" className="wofy-btn-ghost" title="See where your family has lived across generations">
              <MapIcon className="w-4 h-4" /> Migration map
            </button>
            <button onClick={() => setRelModal(true)} data-testid="ft-add-rel" className="wofy-btn-ghost" disabled={members.length < 2}>
              <Link2 className="w-4 h-4" /> Link relationship
            </button>
            <button onClick={() => setEventModal(true)} data-testid="ft-add-event" className="wofy-btn-ghost">
              <Calendar className="w-4 h-4" /> New event
            </button>
            <button onClick={() => setGedcomModal(true)} data-testid="ft-gedcom" className="wofy-btn-ghost">
              <Download className="w-4 h-4" /> GEDCOM
            </button>
            <button onClick={() => setBulkInviteOpen(true)} data-testid="ft-bulk-invite" className="wofy-btn-ghost">
              <Mail className="w-4 h-4" /> Invite family
            </button>
            <button onClick={() => setQrPosterOpen(true)} data-testid="ft-qr-poster" className="wofy-btn-ghost" title="Printable QR poster relatives can scan">
              <Share2 className="w-4 h-4" /> QR poster
            </button>
          </div>
        )}
        <div className="flex gap-2 flex-wrap w-full mt-2 md:w-auto md:mt-0">
          <Link
            to={`/family/${tree.id}/manage`}
            data-testid="ft-manage"
            className="wofy-btn-primary"
          >
            <ShieldCheck className="w-4 h-4" /> Family Management Center
          </Link>
          <Link
            to={`/family/${tree.id}/photos-batch`}
            data-testid="ft-photos-batch"
            className="wofy-btn-ghost"
          >
            <ImageIcon className="w-4 h-4" /> Photos batch
          </Link>
          <button onClick={() => setShareModal(true)} data-testid="ft-share" className="wofy-btn-ghost">
            <Share2 className="w-4 h-4" /> Share
          </button>
          <Link
            to={`/family/${tree.slug || tree.id}/relate`}
            data-testid="ft-relate"
            className="wofy-btn-ghost"
            title="See how two family members are related"
          >
            <Users className="w-4 h-4" /> How are we related?
          </Link>
          <Link
            to={`/family/${tree.slug || tree.id}/calendar`}
            data-testid="ft-calendar"
            className="wofy-btn-ghost"
            title="Birthdays, anniversaries and memorials"
          >
            <Calendar className="w-4 h-4" /> Family calendar
          </Link>
        </div>
      </div>

      {/* Admin quick-start help — compact and dismissible. Only visible to
          tree admins so it doesn't clutter the public view. */}
      {isAdmin && <AdminHelpCard treeSlug={tree.slug || tree.id} />}

      {/* Persistent QR + short-URL card — auto-mints on mount for
          signed-in users. Restaurants use it on printed menus/table
          cards; families use it on invitation cards, funeral programs
          etc. Sits at the top of the tree page so it's easy to scan
          from another device. */}
      <div className="mb-6" data-testid="ft-splink-slot">
        <SplinkCard
          label={`${tree.name} · family tree`}
          headline="Scan or share this tree"
          testId="ft-splink-card"
        />
      </div>

      {/* Page-level search — visible on ALL tabs. Searches by name AND
          location. Selecting a result jumps to the Tree tab and highlights
          the card. */}
      <FamilyTreePageSearch
        members={members}
        treeSlug={tree.slug || tree.id}
        onPickMember={(mid) => {
          setTab("tree");
          setTimeout(() => {
            const el = document.querySelector(`[data-testid="tree-node-${mid}"]`);
            if (!el) return;
            el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
            const prev = el.style.boxShadow;
            el.style.boxShadow = "0 0 0 4px rgba(194,65,12,0.55), 0 12px 32px -8px rgba(194,65,12,0.45)";
            el.style.transition = "box-shadow 350ms ease";
            setTimeout(() => { el.style.boxShadow = prev; }, 3000);
          }, 300);
        }}
        onPickMemberProfile={(mid) => {
          // Default click on a search result now jumps to the member's
          // profile view (per Sep 2026 user feedback). "On tree" chip
          // still opens Tree View + highlights the node.
          navigate(`/family/${tree.slug || tree.id}/member/${mid}`);
        }}
      />

      <div className="flex gap-2 mb-6 border-b overflow-x-auto" style={{ borderColor: "var(--wofy-line)" }}>
        {[
          { v: "tree", label: "Tree View", icon: Users },
          { v: "members", label: "Profile View", icon: UserCircle2 },
          { v: "timeline", label: "Timeline", icon: Clock },
          { v: "events", label: "Events", icon: Calendar },
          { v: "discoveries", label: "Discoveries", icon: Flower2 },
          { v: "albums", label: "Albums", icon: ImageIcon },
          { v: "documents", label: "Documents", icon: BookOpen },
          { v: "archive", label: "Archive", icon: Archive },
          { v: "chat", label: "Family Chat", icon: MessagesSquare },
          { v: "invite", label: "Invite", icon: UserPlus },
          ...(isAdmin ? [{ v: "access", label: "Access", icon: Users }] : []),
        ].map(({ v, label, icon: Icon }) => (
          <button
            key={v}
            data-testid={`ft-tab-${v}`}
            onClick={() => setTab(v)}
            className={`px-3 py-2 text-sm border-b-2 -mb-px inline-flex items-center gap-1.5 whitespace-nowrap`}
            style={{ borderColor: tab === v ? "var(--wofy-primary)" : "transparent", color: tab === v ? "var(--wofy-primary)" : "var(--wofy-text-soft)" }}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {/* Sticky caption / hero header above the active tab's white content
          area. Uses the same tree name shown at the very top so the family
          identity stays visible even when the visitor scrolls into the
          member grid. */}
      {["tree", "members", "timeline", "invite"].includes(tab) && (
        <div className="mb-4" data-testid="ft-content-caption">
          <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>
            {tab === "invite" ? "INVITE FAMILY" : `${tab.toUpperCase()} · ${tree.member_count || members.length} MEMBER${(tree.member_count || members.length) === 1 ? "" : "S"}`}
          </div>
          <h2 className="font-display text-2xl md:text-3xl leading-tight" data-testid="ft-content-caption-name">
            {tree.name}
          </h2>
        </div>
      )}

      {tab === "tree" && <TreeView members={members} rels={rels} tree={tree} isAdmin={isAdmin} onChanged={load} />}
      {tab === "tree" && user && (
        <button
          type="button"
          onClick={goToMyself}
          disabled={whereAmIBusy}
          data-testid="tree-where-am-i-btn"
          className="fixed z-30 flex items-center gap-2 rounded-full font-semibold text-sm px-4 py-3 shadow-lg"
          style={{
            right: 20,
            bottom: 88,           // sits above the mobile bottom nav; harmless on desktop
            background: "#C2410C",
            color: "#fff",
            boxShadow: "0 12px 32px -8px rgba(194,65,12,0.45)",
          }}
          aria-label={myMember ? "Show my place in the family" : "You are not linked to a family member"}
        >
          <MapPin className="w-4 h-4" />
          <span className="hidden sm:inline">{myMember ? "Where am I?" : "Link me"}</span>
        </button>
      )}
      {tab === "timeline" && (
        <div>
          <FamilyUpcoming members={members} rels={rels} events={events} treeName={tree.name || "our family"} treeSlug={tree.slug || tree.id} />
          <FamilyTimeline members={members} rels={rels} events={events} />
        </div>
      )}
      {tab === "members" && <MembersList members={members} isAdmin={isAdmin} treeId={tree.id} treeSlug={tree.slug || tree.id} tree={tree} onDeleted={load} user={user} />}
      {tab === "events" && <EventsList events={events} treeId={tree.id} onChanged={load} isAdmin={isAdmin} />}
      {tab === "discoveries" && <FamilyDiscoveries members={members} events={events} treeSlug={tree.slug || tree.id} onPostEvent={() => setTab("events")} />}
      {tab === "albums" && <AlbumsTab treeId={tree.id} isAdmin={isAdmin} />}
      {tab === "documents" && <DocumentsList treeSlug={tree.slug || tree.id} isAdmin={isAdmin} />}
      {tab === "archive" && <ArchiveTab treeId={tree.id} treeSlug={tree.slug || tree.id} isAdmin={isAdmin} user={user} members={members} />}
      {tab === "chat" && (
        <FamilyChat
          treeId={tree.id}
          isMember={isAdmin || (user && (tree.member_user_ids || []).includes(user.id))}
          canModerate={isAdmin}
        />
      )}
      {tab === "invite" && <InviteTab treeId={tree.id} treeName={tree.name} isAdmin={isAdmin} user={user} />}
      {tab === "access" && isAdmin && <AccessTab treeId={tree.id} />}

      {memberModal && <MemberModal treeId={tree.id} members={members} onClose={() => setMemberModal(false)} onSaved={load} />}
      {bulkInviteOpen && <BulkInviteFamilyModal treeId={tree.id} treeName={tree.name} treeSlug={tree.slug || tree.id} senderName={user?.name || user?.full_name} onClose={() => setBulkInviteOpen(false)} />}
      {qrPosterOpen && (
        <QRInvitePoster
          treeName={tree.name}
          treeSlug={tree.slug || tree.id}
          senderName={user?.name || user?.full_name}
          onClose={() => setQrPosterOpen(false)}
        />
      )}
      {relModal && <RelationshipModal treeId={tree.id} members={members} onClose={() => setRelModal(false)} onSaved={load} />}
      {eventModal && <EventModal treeId={tree.id} onClose={() => setEventModal(false)} onSaved={load} />}
      {shareModal && <ShareModal tree={tree} onClose={() => setShareModal(false)} />}
      {gedcomModal && <GedcomModal tree={tree} isAdmin={isAdmin} onClose={() => setGedcomModal(false)} onImported={load} />}
      {editTreeModal && <EditTreeModal tree={tree} onClose={() => setEditTreeModal(false)} onSaved={(next) => { setTree(next); setEditTreeModal(false); }} />}
      {aiSmartAddOpen && <AiSmartAddModal treeId={tree.id} members={members} onClose={() => setAiSmartAddOpen(false)} onSaved={load} />}
      {migrationMapOpen && <FamilyMigrationMap treeId={tree.id} treeName={tree.name} onClose={() => setMigrationMapOpen(false)} />}

      {/* Mobile-only floating "+" FAB — admins can add a member from anywhere on the tree page.
          Hidden on desktop (header already has the button) and on non-admins.
          Positioned on the LEFT to avoid colliding with QuickReporter/AI Helper FABs at bottom-right. */}
      {isAdmin && !memberModal && !relModal && !eventModal && !bulkInviteOpen && !qrPosterOpen && !shareModal && !gedcomModal && !editTreeModal && (
        <button
          onClick={() => setMemberModal(true)}
          data-testid="ft-fab-add"
          aria-label="Add a family member"
          className="md:hidden fixed z-30 flex items-center gap-2 transition-transform active:scale-95"
          style={{
            left: 20,
            bottom: "calc(84px + env(safe-area-inset-bottom, 0px))",
            height: 56,
            paddingLeft: 18,
            paddingRight: 20,
            borderRadius: 999,
            background: "linear-gradient(135deg, #C2410C 0%, #B23A3A 100%)",
            color: "#fff",
            boxShadow: "0 14px 30px -8px rgba(194,65,12,0.55), 0 2px 6px rgba(0,0,0,0.1)",
            border: "3px solid rgba(255,255,255,0.9)",
            fontWeight: 700,
            fontSize: 14,
          }}
          title="Add a family member"
        >
          <Plus className="w-5 h-5" strokeWidth={2.8} />
          Add member
        </button>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Edit-tree modal — owner/admin can update the tree's name,           */
/* description, visibility, country and alternate/branch labels.       */
/* ------------------------------------------------------------------ */
function EditTreeModal({ tree, onClose, onSaved }) {
  const [name, setName] = useState(tree.name || "");
  const [description, setDescription] = useState(tree.description || "");
  const [visibility, setVisibility] = useState(tree.visibility || "public");
  const [country, setCountry] = useState(tree.country || "");
  const [altNames, setAltNames] = useState((tree.alternate_names || []).join(", "));
  const [branchNames, setBranchNames] = useState((tree.branch_names || []).join(", "));
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Tree name is required."); return; }
    setBusy(true);
    try {
      const { data } = await http.patch(`/family-trees/${tree.id}`, {
        name: name.trim(),
        description: description.trim() || null,
        visibility,
        country: country.trim() || null,
        alternate_names: altNames.split(",").map((s) => s.trim()).filter(Boolean),
        branch_names: branchNames.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Family tree updated.");
      onSaved(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save changes.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose} data-testid="ft-edit-tree-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="wofy-card max-w-lg w-full max-h-[90vh] overflow-y-auto !p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl inline-flex items-center gap-2"><Pencil className="w-4 h-4" /> Edit family tree</h3>
          <button type="button" onClick={onClose} className="opacity-60 hover:opacity-100" aria-label="Close" data-testid="ft-edit-tree-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <label className="block mb-3">
          <span className="text-[11px] font-mono uppercase tracking-wider">Tree name *</span>
          <input required maxLength={120} value={name} onChange={(e) => setName(e.target.value)} className="wofy-input w-full mt-1" data-testid="ft-edit-tree-name" />
        </label>
        <label className="block mb-3">
          <span className="text-[11px] font-mono uppercase tracking-wider">Description</span>
          <textarea rows={3} maxLength={1200} value={description} onChange={(e) => setDescription(e.target.value)} className="wofy-input w-full mt-1" data-testid="ft-edit-tree-description" />
        </label>
        <label className="block mb-3">
          <span className="text-[11px] font-mono uppercase tracking-wider">Country</span>
          <input maxLength={120} value={country} onChange={(e) => setCountry(e.target.value)} className="wofy-input w-full mt-1" data-testid="ft-edit-tree-country" />
        </label>
        <label className="block mb-3">
          <span className="text-[11px] font-mono uppercase tracking-wider">Alternate spellings (comma-separated)</span>
          <input maxLength={400} value={altNames} onChange={(e) => setAltNames(e.target.value)} className="wofy-input w-full mt-1" data-testid="ft-edit-tree-alts" placeholder="Vell, Vellu, वेल" />
        </label>
        <label className="block mb-3">
          <span className="text-[11px] font-mono uppercase tracking-wider">Branch labels (comma-separated)</span>
          <input maxLength={400} value={branchNames} onChange={(e) => setBranchNames(e.target.value)} className="wofy-input w-full mt-1" data-testid="ft-edit-tree-branches" placeholder="Kottayam · Idukki · Munnar" />
        </label>
        <label className="block mb-4">
          <span className="text-[11px] font-mono uppercase tracking-wider">Visibility</span>
          <select value={visibility} onChange={(e) => setVisibility(e.target.value)} className="wofy-input w-full mt-1" data-testid="ft-edit-tree-visibility">
            <option value="public">Public — anyone can view</option>
            <option value="invite">Invite-only — visible but read-only for outsiders</option>
            <option value="private">Private — only members can view</option>
          </select>
        </label>
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="wofy-btn-ghost text-sm">Cancel</button>
          <button type="submit" disabled={busy} className="wofy-btn-primary text-sm" data-testid="ft-edit-tree-save">
            {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Hierarchical family-tree diagram (tidy tree with SVG connectors).   */
/*                                                                     */
/* Layout algorithm — designed to render 100–300 members cleanly:      */
/*   1. Union-find spouse edges → each "family unit" is 1 or 2 people. */
/*   2. Each unit picks ONE "primary parent unit" (the parent-unit of  */
/*      its earliest-born blood-line member). This turns the DAG into  */
/*      a rendering tree, avoiding duplicate placements.               */
/*   3. Bottom-up: compute subtree-width = max(sum(children widths) +  */
/*      sibling gaps, own width).                                      */
/*   4. Top-down: place each root at cursorX; place children left-to-  */
/*      right within the root's subtree; center each parent above its  */
/*      children.                                                      */
/*   5. Render nodes as absolute-positioned cards; draw orthogonal     */
/*      SVG connectors (down-stub → horizontal bus → down-stub) from   */
/*      each parent unit to each child unit.                           */
/*   6. Pan via mouse-drag on the canvas background; zoom via toolbar  */
/*      buttons and Ctrl/⌘ + wheel.                                    */
/* ------------------------------------------------------------------ */
/* Layout constants (TREE_CARD_W/H, GEN_GAP, …) and HERITAGE_PALETTE
 * now live in @/components/family-tree/treeConstants so the extracted
 * TreeMiniCard / QuickEditDrawer components can share them. */

function MenuRow({ icon: Icon, label, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid} className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left hover:bg-black/[0.03]">
      <Icon className="w-4 h-4 shrink-0" style={{ color: "#B25D38" }} /> {label}
    </button>
  );
}

/**
 * MemberPopup — MyHeritage-style rich side panel. Flush under the header
 * (no dead space), scrollable, with quick actions (Profile / Edit / Add /
 * More), a personalised birthday card, and a photo strip with "+ Add".
 */
function MemberPopup({ m, treeSlug, treeId, isAdmin, onClose, onLocate, onEdit }) {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [contribKind, setContribKind] = useState(null);
  const [wishOccasion, setWishOccasion] = useState(null);
  const [menu, setMenu] = useState(null); // "add" | "more" | null
  const name = [m.first_name, m.middle_name, m.last_name].filter(Boolean).join(" ");
  const y1 = (m.birth_date || "").slice(0, 4);
  const y2 = (m.death_date || "").slice(0, 4);

  const loadPhotos = useCallback(() => {
    if (!treeId) return;
    http.get(`/family-trees/${treeId}/contributions`, { params: { member_id: m.id, kind: "photo", limit: 12 } })
      .then((r) => setPhotos(r.data.items || [])).catch(() => {});
  }, [treeId, m.id]);

  useEffect(() => {
    setProfile(null); setMenu(null); setContribKind(null); setPhotos([]);
    if (!treeId) return;
    http.get(`/family-trees/${treeId}/members/${m.id}/profile`).then((r) => setProfile(r.data)).catch(() => {});
    loadPhotos();
  }, [m.id, treeId, loadPhotos]);

  const isYou = !!profile?.is_you;
  const editable = !!profile?.editable || !!isAdmin;

  let age = null;
  if (m.birth_date && y1 && !m.death_date) {
    const b = new Date(m.birth_date.length === 4 ? `${m.birth_date}-01-01` : m.birth_date.slice(0, 10));
    if (!isNaN(b)) {
      const now = new Date();
      age = now.getFullYear() - b.getFullYear() - ((now.getMonth() < b.getMonth() || (now.getMonth() === b.getMonth() && now.getDate() < b.getDate())) ? 1 : 0);
    }
  }
  const birthLabel = m.birth_date
    ? (m.birth_date_is_year_only || m.birth_date.length === 4
        ? y1
        : new Date(m.birth_date.slice(0, 10)).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }))
    : null;

  let countdown = null;
  if (!m.death_date && m.birth_date && !m.birth_date_is_year_only && m.birth_date.length >= 8) {
    const [, mm, dd] = m.birth_date.split("-");
    if (mm && dd) {
      const now = new Date(); now.setHours(0, 0, 0, 0);
      let next = new Date(now.getFullYear(), Number(mm) - 1, Number(dd));
      if (next < now) next = new Date(now.getFullYear() + 1, Number(mm) - 1, Number(dd));
      countdown = {
        days: Math.round((next - now) / 86400000),
        turning: next.getFullYear() - Number(y1),
        dateLabel: next.toLocaleDateString(undefined, { month: "long", day: "numeric" }),
      };
    }
  }

  const gallery = [
    ...(m.photo ? [{ id: "profile", url: m.photo, caption: "Profile photo" }] : []),
    ...photos.filter((p) => p.url && p.url !== m.photo),
  ];

  const RoundBtn = ({ icon: Icon, label, onClick, testid, active }) => (
    <button type="button" onClick={onClick} data-testid={testid} className="flex flex-col items-center gap-1.5 flex-1 group">
      <span
        className="w-11 h-11 rounded-full border flex items-center justify-center transition group-hover:shadow-md group-hover:-translate-y-0.5"
        style={{ borderColor: "var(--wofy-border)", background: active ? "#FFF7ED" : "#FAFAF7" }}
      >
        <Icon className="w-5 h-5" style={{ color: "#B25D38" }} />
      </span>
      <span className="text-[11px] opacity-80">{label}</span>
    </button>
  );

  return (
    <div
      className="fixed right-2 top-14 bottom-2 z-[97] w-80 max-w-[94vw] rounded-2xl bg-white shadow-2xl border flex flex-col overflow-hidden"
      style={{ borderColor: "var(--wofy-border)" }}
      data-testid="member-popup"
    >
      <button
        onClick={onClose}
        className="absolute top-2.5 right-2.5 z-10 w-7 h-7 rounded-full bg-white/90 border flex items-center justify-center opacity-70 hover:opacity-100"
        style={{ borderColor: "var(--wofy-border)" }}
        data-testid="member-popup-close"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex-1 overflow-y-auto p-4">
        {/* Identity */}
        <div className="flex items-start gap-3 pr-6">
          <AncestorAvatar member={m} size={64} showAddChip={false} className="!rounded-full shrink-0" />
          <div className="min-w-0">
            <div className="font-display text-lg leading-tight" data-testid="member-popup-name">{name}</div>
            {isYou && <div className="text-[11px] font-semibold mt-0.5" style={{ color: "#16A34A" }} data-testid="member-popup-isyou">This is you</div>}
            {birthLabel && (
              <div className="text-xs opacity-70 mt-1">
                ✦ {birthLabel}{age != null ? ` (age ${age})` : ""}
              </div>
            )}
            {(m.birth_place || m.origin_location || m.location) && (
              <div className="text-xs opacity-60 mt-0.5 flex items-center gap-1">
                <MapPin className="w-3 h-3 shrink-0" />
                <span className="truncate">{m.birth_place || m.origin_location || m.location}</span>
              </div>
            )}
            {m.death_date && (
              <div className="text-[11px] mt-0.5" style={{ color: "#7C3AED" }}>
                In loving memory{y1 && y2 ? ` · ${y1} – ${y2}` : ""}
              </div>
            )}
            <Link
              to={`/family/${treeSlug}/member/${m.id}`}
              className="inline-block text-xs font-semibold mt-1.5 hover:underline"
              style={{ color: "#B25D38" }}
              data-testid="member-popup-research"
            >
              Research this person ›
            </Link>
          </div>
        </div>

        {/* Action row */}
        <div className="mt-4 flex items-start gap-1 border-y py-3" style={{ borderColor: "var(--wofy-border)" }}>
          <RoundBtn icon={UserCircle2} label="Profile" testid="member-popup-profile" onClick={() => navigate(`/family/${treeSlug}/member/${m.id}`)} />
          {editable && (
            <RoundBtn icon={Pencil} label="Edit" testid="member-popup-edit" onClick={() => navigate(`/family/${treeSlug}/member/${m.id}/edit`)} />
          )}
          <RoundBtn icon={Plus} label="Add" testid="member-popup-add" active={menu === "add"} onClick={() => setMenu(menu === "add" ? null : "add")} />
          <RoundBtn icon={MoreHorizontal} label="More" testid="member-popup-more" active={menu === "more"} onClick={() => setMenu(menu === "more" ? null : "more")} />
        </div>

        {menu === "add" && (
          <div className="mt-2 rounded-xl border divide-y overflow-hidden" style={{ borderColor: "var(--wofy-border)" }} data-testid="member-popup-add-menu">
            <MenuRow icon={Calendar} label="Send a wish (occasions)" onClick={() => { setMenu(null); navigate(`/family/${treeSlug}/wishes?member=${m.id}`); }} testid="member-popup-send-wish" />
            <MenuRow icon={ImageIcon} label="Add a photo" onClick={() => { setMenu(null); setContribKind("photo"); }} testid="member-popup-add-photo" />
            <MenuRow icon={FileText} label="Add a document" onClick={() => { setMenu(null); setContribKind("document"); }} testid="member-popup-add-document" />
            <MenuRow icon={MessageCircle} label="Ask the family admin" onClick={() => { setMenu(null); setContribKind("message"); }} testid="member-popup-ask-admin" />
          </div>
        )}
        {menu === "more" && (
          <div className="mt-2 rounded-xl border divide-y overflow-hidden" style={{ borderColor: "var(--wofy-border)" }} data-testid="member-popup-more-menu">
            <MenuRow icon={MapPin} label="Show in tree" onClick={() => { setMenu(null); onLocate?.(); }} testid="member-popup-locate" />
            {onEdit && <MenuRow icon={Pencil} label="Quick edit (admin)" onClick={() => { setMenu(null); onEdit(); }} testid="member-popup-quickedit" />}
            <MenuRow
              icon={Copy}
              label="Copy profile link"
              onClick={() => {
                setMenu(null);
                const url = `${window.location.origin}/family/${treeSlug}/member/${m.id}`;
                if (navigator.clipboard?.writeText) {
                  navigator.clipboard.writeText(url).then(() => toast.success("Link copied")).catch(() => toast.error("Copy failed — please copy manually: " + url));
                } else {
                  toast.info(url);
                }
              }}
              testid="member-popup-copylink"
            />
            <MenuRow icon={MessageCircle} label="Message the admin" onClick={() => { setMenu(null); setContribKind("message"); }} testid="member-popup-message-admin" />
          </div>
        )}

        {/* Birthday card — tap to send wishes */}
        {countdown && (
          <button
            type="button"
            onClick={() => {
              const t = countdown.turning;
              const suf = t % 100 >= 11 && t % 100 <= 13 ? "th" : ({ 1: "st", 2: "nd", 3: "rd" }[t % 10] || "th");
              setWishOccasion({ category_key: "birthdays", label: countdown.days === 0 ? "Birthday" : `${t}${suf} birthday` });
            }}
            className="mt-3 w-full text-left rounded-xl px-3 py-2.5 flex items-start gap-2.5 transition hover:shadow-sm"
            style={{ background: "#FFF1F0" }}
            data-testid="member-popup-birthday"
          >
            <span className="text-xl leading-none mt-0.5">🎂</span>
            <div className="text-xs" style={{ color: "#92400E" }}>
              <div className="font-semibold">
                {isYou ? `You are turning ${countdown.turning} on ${countdown.dateLabel}` : `${m.first_name || "They"} turns ${countdown.turning} on ${countdown.dateLabel}`}
                {countdown.days === 0 ? " — TODAY!" : ` (in ${countdown.days} day${countdown.days === 1 ? "" : "s"})`}
              </div>
              <div className="mt-0.5 opacity-80">{isYou ? "Congratulations!!!" : "Tap to send birthday wishes →"}</div>
            </div>
          </button>
        )}

        {/* Discoveries teaser */}
        <div className="mt-4">
          <div className="text-[10px] font-mono uppercase tracking-widest opacity-60">Discoveries</div>
          <Link
            to={`/family/${treeSlug}/member/${m.id}`}
            className="mt-1.5 flex items-center gap-2 text-xs hover:underline"
            style={{ color: "#B25D38" }}
            data-testid="member-popup-discoveries"
          >
            <BookOpen className="w-3.5 h-3.5" /> View timeline, stats &amp; family stories
          </Link>
        </div>

        {/* Photos & videos */}
        <div className="mt-4" data-testid="member-popup-photos">
          <div className="flex items-center justify-between">
            <div className="text-[10px] font-mono uppercase tracking-widest opacity-60">Photos &amp; Videos ({gallery.length})</div>
            <button type="button" onClick={() => setContribKind("photo")} className="text-xs font-semibold hover:underline" style={{ color: "#B25D38" }} data-testid="member-popup-photos-add">
              + Add
            </button>
          </div>
          {gallery.length > 0 ? (
            <div className="mt-2 grid grid-cols-3 gap-1.5">
              {gallery.slice(0, 9).map((p) => (
                <a key={p.id} href={resolveImage(p.url)} target="_blank" rel="noreferrer" className="block rounded-lg overflow-hidden border" style={{ borderColor: "var(--wofy-border)" }}>
                  <img src={resolveImage(p.url)} alt={p.caption || ""} className="w-full h-20 object-cover hover:opacity-90 transition-opacity" loading="lazy" />
                </a>
              ))}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setContribKind("photo")}
              className="mt-2 w-full rounded-xl border-2 border-dashed py-4 text-xs opacity-70 hover:opacity-100"
              style={{ borderColor: "var(--wofy-border)" }}
              data-testid="member-popup-photos-empty-add"
            >
              No photos yet — add the first one
            </button>
          )}
        </div>
      </div>

      {contribKind && (
        <MemberContributeModal
          kind={contribKind}
          treeId={treeId}
          memberId={m.id}
          memberName={m.first_name}
          onClose={() => setContribKind(null)}
          onDone={() => { setContribKind(null); loadPhotos(); toast.success("Added — thank you!"); }}
        />
      )}
      {wishOccasion && (
        <WishComposerModal
          treeId={treeId}
          member={m}
          occasion={wishOccasion}
          onClose={() => setWishOccasion(null)}
        />
      )}
    </div>
  );
}

function BulkInviteFamilyModal({ treeId, treeName, treeSlug, senderName, onClose }) {
  const [mode, setMode] = useState("email"); // "email" | "whatsapp"
  const [emailsText, setEmailsText] = useState("");
  const [message, setMessage] = useState("");
  const [role, setRole] = useState("viewer");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [posterOpen, setPosterOpen] = useState(false);

  // Parse pasted list — accept comma / newline / space / semicolon.
  // We surface TWO counts: total unique (`parsed`) and valid-looking
  // emails (`validCount`) so the send-count matches what will actually
  // be delivered.
  const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
  const parsed = useMemo(() => {
    if (mode === "whatsapp") {
      // Split by newlines only so 'phone ; name' pairs survive.
      return (emailsText || "").split(/\n+/).map((s) => s.trim()).filter(Boolean);
    }
    const raw = (emailsText || "").split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    const seen = new Set();
    const out = [];
    for (const e of raw) {
      const k = e.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(e);
    }
    return out;
  }, [emailsText, mode]);

  const validCount = useMemo(() => {
    if (mode === "whatsapp") {
      // A phone entry is valid when digits >= 8 (ignoring pipe/name).
      return parsed.filter((row) => {
        const phone = row.split(/[;·|]/)[0] || "";
        return (phone.replace(/\D+/g, "").length) >= 8;
      }).length;
    }
    return parsed.filter((e) => EMAIL_RE.test(e)).length;
  }, [parsed, mode]);

  const canSend = !busy && parsed.length > 0;

  const submit = async () => {
    if (!canSend || validCount === 0) return;
    setBusy(true);
    setResult(null);
    try {
      if (mode === "whatsapp") {
        const { data } = await http.post(`/family-trees/${treeId}/bulk-invite-whatsapp`, {
          contacts: parsed,
          message: message.trim() || null,
          role,
        });
        setResult({ ...data, mode: "whatsapp" });
        toast.success(`${data?.totals?.prepared ?? 0} WhatsApp invite${(data?.totals?.prepared ?? 0) === 1 ? "" : "s"} ready.`);
      } else {
        const { data } = await http.post(`/family-trees/${treeId}/bulk-invite`, {
          emails: parsed,
          message: message.trim() || null,
          role,
        });
        setResult({ ...data, mode: "email" });
        toast.success(`Sent ${data?.totals?.sent ?? 0} invite${(data?.totals?.sent ?? 0) === 1 ? "" : "s"}.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send invites.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end md:items-center justify-center px-2 md:px-4" onClick={onClose} data-testid="bulk-invite-modal">
      <div
        className="wofy-card w-full max-w-lg max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2" style={{ color: "var(--wofy-primary)" }}>
              <Mail className="w-5 h-5" /> Invite family to <em>{treeName}</em>
            </h3>
            <p className="text-xs opacity-70 mt-1">Paste any number of email addresses — one per line, or separated by commas / spaces. We'll de-dupe automatically and skip anyone invited in the last 24 hours.</p>
          </div>
          <button onClick={onClose} className="wofy-btn-ghost !p-1" data-testid="bulk-invite-close"><X className="w-4 h-4" /></button>
        </div>

        {!result ? (
          <div className="space-y-3">
            {/* Channel toggle — Email vs WhatsApp broadcast. */}
            <div className="flex gap-1 rounded-full border p-0.5" style={{ borderColor: "var(--wofy-border)", background: "rgba(0,0,0,0.02)" }}>
              {[
                { v: "email", label: "Email invites" },
                { v: "whatsapp", label: "WhatsApp invites" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => { setMode(opt.v); setEmailsText(""); setResult(null); }}
                  data-testid={`bulk-invite-mode-${opt.v}`}
                  className="flex-1 px-3 py-1.5 rounded-full text-xs"
                  style={{
                    background: mode === opt.v ? "var(--wofy-primary)" : "transparent",
                    color: mode === opt.v ? "white" : "inherit",
                    fontWeight: mode === opt.v ? 600 : 400,
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <label className="block text-xs">
              <span className="font-mono uppercase tracking-wider opacity-70">
                {mode === "whatsapp" ? "Phone numbers (one per line)" : "Emails"}
              </span>
              <textarea
                rows={5}
                value={emailsText}
                onChange={(e) => setEmailsText(e.target.value)}
                placeholder={mode === "whatsapp"
                  ? "e.g.\n+91 98450 12345 ; Kunju uncle\n+1 408 555 0100 ; Anna"
                  : "e.g. tomvell@gmail.com, saji@example.com\nanitha@example.com"}
                className="mt-1 w-full px-2.5 py-2 rounded-lg border text-sm font-mono"
                style={{ borderColor: "var(--wofy-border)" }}
                data-testid="bulk-invite-emails"
              />
            </label>
            <div className="text-[11px] opacity-70">
              {validCount > 0
                ? `${validCount} ${mode === "whatsapp" ? "phone number" : "unique address"}${validCount === 1 ? "" : "s"} ready to invite${parsed.length - validCount > 0 ? ` · ${parsed.length - validCount} will be skipped as invalid` : ""}.`
                : parsed.length > 0
                  ? mode === "whatsapp"
                    ? "None of the pasted values look like phone numbers — please check."
                    : "None of the pasted values look like an email — please check."
                  : mode === "whatsapp"
                    ? "Paste one phone per line. Add a name after ‘;’ to personalise, e.g. +91 98… ; Anna aunty."
                    : "No addresses yet."}
            </div>

            <label className="block text-xs">
              <span className="font-mono uppercase tracking-wider opacity-70">Personal note (optional)</span>
              <textarea
                rows={3}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={1500}
                placeholder="Say hi in your own words — this goes at the top of the invite."
                className="mt-1 w-full px-2.5 py-2 rounded-lg border text-sm"
                style={{ borderColor: "var(--wofy-border)" }}
                data-testid="bulk-invite-message"
              />
            </label>

            <div className="flex items-center gap-2 text-xs">
              <span className="font-mono uppercase tracking-wider opacity-70">Access</span>
              {[
                { v: "viewer", label: "View only" },
                { v: "editor", label: "Can edit" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setRole(opt.v)}
                  data-testid={`bulk-invite-role-${opt.v}`}
                  className="px-3 py-1 rounded-full border text-xs"
                  style={{
                    borderColor: role === opt.v ? "var(--wofy-primary)" : "var(--wofy-border)",
                    background: role === opt.v ? "rgba(194,65,12,0.10)" : "white",
                    color: role === opt.v ? "var(--wofy-primary)" : "inherit",
                    fontWeight: role === opt.v ? 600 : 400,
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} className="wofy-btn-ghost" disabled={busy}>Cancel</button>
              <button
                onClick={submit}
                disabled={!canSend || validCount === 0}
                className="wofy-btn-primary"
                data-testid="bulk-invite-send"
              >
                {busy
                  ? "Sending…"
                  : mode === "whatsapp"
                    ? `Prepare ${validCount || ""} WhatsApp invite${validCount === 1 ? "" : "s"}`
                    : `Send ${validCount || ""} invite${validCount === 1 ? "" : "s"}`}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            {result?.mode === "whatsapp" ? (
              <>
                <div className="rounded-lg p-3" style={{ background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.25)" }}>
                  <div className="font-semibold text-emerald-700">Ready — {result?.totals?.prepared ?? 0} WhatsApp invite{(result?.totals?.prepared ?? 0) === 1 ? "" : "s"} prepared.</div>
                  <div className="text-xs opacity-80 mt-1">Tap each button to open WhatsApp with the message pre-filled. Nothing is sent server-side.</div>
                </div>
                <ul className="space-y-1.5" data-testid="bulk-invite-whatsapp-list">
                  {(result?.invites || []).map((r, i) => (
                    <li key={i} className="flex items-center gap-2 border rounded-lg px-3 py-2" style={{ borderColor: "var(--wofy-border)" }}>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{r.name || `+${r.phone}`}</div>
                        <div className="text-[11px] opacity-60">+{r.phone}</div>
                      </div>
                      <a
                        href={r.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded text-white"
                        style={{ background: "#25D366" }}
                        data-testid={`bulk-invite-whatsapp-open-${i}`}
                      >
                        Open in WhatsApp
                      </a>
                    </li>
                  ))}
                </ul>
                {result?.invalid?.length > 0 && (
                  <div>
                    <div className="text-xs font-mono uppercase tracking-wider opacity-70 mb-1">Invalid — didn't look like a phone</div>
                    <ul className="text-xs opacity-80 list-disc pl-5">
                      {result.invalid.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="rounded-lg p-3" style={{ background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.25)" }}>
                  <div className="font-semibold text-emerald-700">Done — {result?.totals?.sent ?? 0} invite{(result?.totals?.sent ?? 0) === 1 ? "" : "s"} sent.</div>
                </div>
                {result?.sent?.length > 0 && (
                  <div>
                    <div className="text-xs font-mono uppercase tracking-wider opacity-70 mb-1">Delivered</div>
                    <ul className="text-xs opacity-80 list-disc pl-5" data-testid="bulk-invite-delivered">
                      {result.sent.map((e) => <li key={e}>{e}</li>)}
                    </ul>
                  </div>
                )}
                {result?.skipped_24h?.length > 0 && (
                  <div>
                    <div className="text-xs font-mono uppercase tracking-wider opacity-70 mb-1">Skipped — already invited in the last 24h</div>
                    <ul className="text-xs opacity-80 list-disc pl-5" data-testid="bulk-invite-skipped">
                      {result.skipped_24h.map((e) => <li key={e}>{e}</li>)}
                    </ul>
                  </div>
                )}
                {result?.invalid?.length > 0 && (
                  <div>
                    <div className="text-xs font-mono uppercase tracking-wider opacity-70 mb-1">Invalid — didn't look like emails</div>
                    <ul className="text-xs opacity-80 list-disc pl-5" data-testid="bulk-invite-invalid">
                      {result.invalid.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  </div>
                )}
                {result?.failed?.length > 0 && (
                  <div>
                    <div className="text-xs font-mono uppercase tracking-wider text-red-600 mb-1">Failed to deliver</div>
                    <ul className="text-xs text-red-700 list-disc pl-5">
                      {result.failed.map((f, i) => <li key={i}>{f.email} — {f.error}</li>)}
                    </ul>
                  </div>
                )}
              </>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setPosterOpen(true)}
                className="wofy-btn-ghost text-xs inline-flex items-center gap-1"
                data-testid="bulk-invite-poster"
                title="Print a QR poster relatives can scan at reunions"
              >
                <Share2 className="w-3.5 h-3.5" /> QR poster
              </button>
              <button onClick={() => { setResult(null); setEmailsText(""); setMessage(""); }} className="wofy-btn-ghost" data-testid="bulk-invite-send-more">Send another batch</button>
              <button onClick={onClose} className="wofy-btn-primary" data-testid="bulk-invite-done">Done</button>
            </div>
          </div>
        )}
        {posterOpen && (
          <QRInvitePoster
            treeName={treeName}
            treeSlug={treeSlug}
            senderName={senderName}
            recipients={
              result?.mode === "whatsapp"
                ? (result?.invites || []).map((i) => ({ name: i.name, phone: i.phone }))
                : (result?.delivered || []).map((d) => ({ email: d.email, token: d.token }))
            }
            onClose={() => setPosterOpen(false)}
          />
        )}
      </div>
    </div>
  );
}

function AdminHelpCard({ treeSlug }) {
  const dismissKey = `ft-admin-help-dismissed-${treeSlug || "any"}`;
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(dismissKey) === "1"; } catch { return false; }
  });
  const [open, setOpen] = useState(false);
  if (dismissed) return null;
  return (
    <div
      className="mb-4 rounded-xl border p-3 md:p-4"
      style={{
        borderColor: "var(--wofy-primary)",
        background: "linear-gradient(135deg, rgba(194,65,12,0.06), rgba(139,92,246,0.04))",
      }}
      data-testid="ft-admin-help"
    >
      <div className="flex items-center gap-2">
        <div className="text-lg" aria-hidden>🌳</div>
        <div className="flex-1">
          <div className="text-sm font-semibold" style={{ color: "var(--wofy-primary)" }}>
            Managing this family tree — quick start
          </div>
          <div className="text-[11px] opacity-80">
            Adding people, moving generations, editing places — all right here.
          </div>
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-xs underline opacity-80 hover:opacity-100"
          data-testid="ft-admin-help-toggle"
        >
          {open ? "Hide" : "Show tips"}
        </button>
        <button
          type="button"
          onClick={() => { try { localStorage.setItem(dismissKey, "1"); } catch {} setDismissed(true); }}
          className="text-xs opacity-60 hover:opacity-100 px-2"
          data-testid="ft-admin-help-dismiss"
          title="Don't show again"
        >
          ✕
        </button>
      </div>
      {open && (
        <ul className="mt-3 space-y-1.5 text-xs opacity-90" data-testid="ft-admin-help-body">
          <li>
            <strong>Add a person</strong> — tap <em>Add member</em>. Pick where they fit
            (parent / child / spouse / sibling of an existing person, or unlinked to link later).
            Generation is auto-computed from the placement.
          </li>
          <li>
            <strong>Current location + Birth place</strong> — both show on every badge.
            Edit either by tapping a card ⇒ pencil icon (or Quick-edit).
          </li>
          <li>
            <strong>Generation label (G-1, G-2 …)</strong> — computed automatically from
            the tree depth. Override it per person from Quick-edit when needed.
          </li>
          <li>
            <strong>Search</strong> — use the box just below to jump to anyone by
            <em> name</em> or <em>place</em> (birth or current). It works on every tab.
          </li>
          <li>
            <strong>Link two people</strong> after adding — tap <em>Link relationship</em>
            to draw parent/spouse/sibling lines. The tree layout updates instantly.
          </li>
        </ul>
      )}
    </div>
  );
}


/**
 * OrphanCard — wraps TreeMiniCard for the "Not yet linked" strip and
 * layers on a delete affordance so tree admins can prune unlinked
 * members without leaving the canvas.
 */
function OrphanCard({ m, treeSlug, treeId, isAdmin, onChanged, onQuickEdit, onOpen }) {
  const [busy, setBusy] = useState(false);
  const del = async (e) => {
    e.preventDefault(); e.stopPropagation();
    const name = [m.first_name, m.last_name].filter(Boolean).join(" ") || "this person";
    if (!window.confirm(`Delete "${name}" permanently from this tree? This can't be undone.`)) return;
    setBusy(true);
    try {
      await http.delete(`/family-trees/${treeId}/members/${m.id}`);
      toast.success(`Removed ${name} from the tree.`);
      if (typeof onChanged === "function") onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="relative" style={{ width: TREE_CARD_W, height: TREE_CARD_H }}>
      <TreeMiniCard m={m} treeSlug={treeSlug} deceased={!!m.death_date} isAdmin={isAdmin} onQuickEdit={onQuickEdit} onOpen={onOpen} />
      {isAdmin && (
        <button
          type="button"
          onClick={del}
          disabled={busy}
          data-testid={`orphan-delete-${m.id}`}
          aria-label="Delete unlinked person"
          title="Delete this unlinked person"
          className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/95 border shadow-sm flex items-center justify-center hover:scale-110 transition disabled:opacity-40"
          style={{ borderColor: "#E5B4B4", zIndex: 5 }}
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" style={{ color: "#B23A3A" }} />}
        </button>
      )}
    </div>
  );
}


/* Build the tidy layout: returns { units:Map, roots:[id], width, height,
 * orphans:[memberId], orphanUnitIds:Set }.
 * `collapsedIds` (Set<unitId>) tells the layout to hide the descendants
 * of those units — a collapsed unit still gets a position, but its
 * subtree is skipped for width/placement so downstream siblings can
 * reclaim the horizontal real estate.
 */
function buildFamilyLayout(members, rels, collapsedIds) {
  const isCollapsed = (uid) => (collapsedIds ? collapsedIds.has(uid) : false);
  const spouseEdges = rels.filter((r) => r.type === "spouse");
  const parentEdges = rels.filter((r) => r.type === "parent");

  // Union-find over members via spouse edges → couple = 1 unit.
  const parent = new Map();
  const find = (x) => {
    if (!parent.has(x)) parent.set(x, x);
    let r = x;
    while (parent.get(r) !== r) r = parent.get(r);
    let cur = x;
    while (parent.get(cur) !== r) { const nxt = parent.get(cur); parent.set(cur, r); cur = nxt; }
    return r;
  };
  const union = (a, b) => { const ra = find(a); const rb = find(b); if (ra !== rb) parent.set(ra, rb); };
  members.forEach((m) => find(m.id));
  spouseEdges.forEach((e) => union(e.person1_id, e.person2_id));

  const memberMap = new Map(members.map((m) => [m.id, m]));

  // Group members into units. Cap size to 2 (defensive: extra spouses go
  // to their own unit via the earliest linked spouse).
  const unitById = new Map();
  members.forEach((m) => {
    const uid = find(m.id);
    if (!unitById.has(uid)) unitById.set(uid, { id: uid, members: [], children: [], parentUnitId: null, x: 0, y: 0, w: TREE_CARD_W });
    unitById.get(uid).members.push(m.id);
  });

  // Sort within-unit: male-first, then earliest birth year.
  for (const u of unitById.values()) {
    u.members.sort((a, b) => {
      const ma = memberMap.get(a); const mb = memberMap.get(b);
      const ga = (ma?.gender || "").toLowerCase();
      const gb = (mb?.gender || "").toLowerCase();
      if (ga !== gb) { if (ga === "male") return -1; if (gb === "male") return 1; }
      return (ma?.birth_date || "").localeCompare(mb?.birth_date || "");
    });
    if (u.members.length > 2) u.members = u.members.slice(0, 2);
    u.w = u.members.length >= 2 ? TREE_CARD_W * 2 + COUPLE_GAP : TREE_CARD_W;
  }

  const memberToUnit = new Map();
  for (const u of unitById.values()) for (const mid of u.members) memberToUnit.set(mid, u.id);

  // For each member with parents, pick the first parent's unit.
  const memberParentUnit = new Map();
  for (const e of parentEdges) {
    if (!memberParentUnit.has(e.person2_id)) {
      const pUnit = memberToUnit.get(e.person1_id);
      if (pUnit) memberParentUnit.set(e.person2_id, pUnit);
    }
  }

  // Choose ONE primary parent-unit per unit: the parent-unit of the
  // earliest-born (or first-listed) member of the unit that has parents.
  for (const u of unitById.values()) {
    let bestMid = null;
    let bestBirth = null;
    for (const mid of u.members) {
      if (memberParentUnit.has(mid)) {
        const bd = memberMap.get(mid)?.birth_date || "";
        if (bestMid == null || (bd && (!bestBirth || bd < bestBirth))) {
          bestMid = mid; bestBirth = bd || bestBirth;
        }
      }
    }
    if (bestMid != null) {
      const pu = memberParentUnit.get(bestMid);
      if (pu && pu !== u.id) u.parentUnitId = pu;
    }
  }

  // Attach children arrays and break any accidental cycles.
  const isDescendant = (ancestorId, targetId) => {
    // Does targetId appear in the descendants of ancestorId?
    if (ancestorId === targetId) return true;
    const stack = [ancestorId];
    const seen = new Set();
    while (stack.length) {
      const cur = stack.pop();
      if (seen.has(cur)) continue;
      seen.add(cur);
      const cu = unitById.get(cur);
      if (!cu) continue;
      for (const cid of cu.children) {
        if (cid === targetId) return true;
        stack.push(cid);
      }
    }
    return false;
  };
  for (const u of unitById.values()) {
    if (u.parentUnitId && unitById.has(u.parentUnitId)) {
      // Guard: if attaching would create a cycle, drop the link (u becomes a root).
      if (isDescendant(u.id, u.parentUnitId)) { u.parentUnitId = null; continue; }
      unitById.get(u.parentUnitId).children.push(u.id);
    }
  }

  // Sort siblings by earliest birth year of primary member.
  for (const u of unitById.values()) {
    u.children.sort((a, b) => {
      const ua = unitById.get(a); const ub = unitById.get(b);
      const ba = memberMap.get(ua.members[0])?.birth_date || "";
      const bb = memberMap.get(ub.members[0])?.birth_date || "";
      return ba.localeCompare(bb);
    });
  }

  const roots = [];
  for (const u of unitById.values()) if (!u.parentUnitId) roots.push(u.id);

  // Total descendants per unit (ignoring collapse — used for the "+N hidden"
  // chip on collapse toggles). Memoized via post-order traversal.
  const totalDescCache = new Map();
  const totalDesc = (uid, seen = new Set()) => {
    if (totalDescCache.has(uid)) return totalDescCache.get(uid);
    if (seen.has(uid)) return 0;
    seen.add(uid);
    const u = unitById.get(uid);
    if (!u || !u.children.length) { totalDescCache.set(uid, 0); return 0; }
    let n = 0;
    for (const c of u.children) n += 1 + totalDesc(c, seen);
    totalDescCache.set(uid, n);
    return n;
  };
  for (const u of unitById.values()) u.descendantCount = totalDesc(u.id);

  // Sort roots: those with the most descendants first (main branch on left).
  roots.sort((a, b) => (unitById.get(b).descendantCount) - (unitById.get(a).descendantCount));

  // Compute subtree widths (respecting collapsed subtrees).
  const swCache = new Map();
  const subtreeW = (uid) => {
    if (swCache.has(uid)) return swCache.get(uid);
    const u = unitById.get(uid);
    if (!u) return 0;
    if (isCollapsed(uid) || !u.children.length) { swCache.set(uid, u.w); return u.w; }
    let total = 0;
    for (let i = 0; i < u.children.length; i++) {
      total += subtreeW(u.children[i]);
      if (i > 0) total += SIBLING_GAP;
    }
    const w = Math.max(total, u.w);
    swCache.set(uid, w);
    return w;
  };

  // Top-down placement.
  const placed = new Set();
  const place = (uid, startX, depth) => {
    const u = unitById.get(uid);
    if (!u) return;
    placed.add(uid);
    u.depth = depth;
    u.y = depth * ROW_H;
    const sw = subtreeW(uid);
    if (isCollapsed(uid) || !u.children.length) {
      u.x = startX + (sw - u.w) / 2;
      return;
    }
    let cursor = startX;
    for (const cid of u.children) {
      const cw = subtreeW(cid);
      place(cid, cursor, depth + 1);
      cursor += cw + SIBLING_GAP;
    }
    const first = unitById.get(u.children[0]);
    const last = unitById.get(u.children[u.children.length - 1]);
    const cx = (first.x + first.w / 2 + last.x + last.w / 2) / 2;
    u.x = cx - u.w / 2;
    if (u.x < startX) u.x = startX + (sw - u.w) / 2;
  };

  let cursorX = 0;
  for (const rid of roots) {
    place(rid, cursorX, 0);
    cursorX += subtreeW(rid) + SIBLING_GAP * 2;
  }

  // Shift by padding — only units that were actually placed.
  let maxX = 0, maxY = 0;
  for (const u of unitById.values()) {
    if (!placed.has(u.id)) { u.x = null; u.y = null; continue; }
    u.x += H_PAD;
    u.y += V_PAD;
    maxX = Math.max(maxX, u.x + u.w);
    maxY = Math.max(maxY, u.y + TREE_CARD_H);
  }

  // Orphans: members whose unit is a root AND has no children AND no
  // spouse (unit size 1). Detach from the canvas — render as a chip strip.
  const orphans = [];
  for (const u of unitById.values()) {
    if (u.members.length === 1 && !u.parentUnitId && u.children.length === 0) {
      orphans.push(u.members[0]);
    }
  }
  const orphanUnitIds = new Set(
    Array.from(unitById.values())
      .filter((u) => u.members.length === 1 && !u.parentUnitId && u.children.length === 0)
      .map((u) => u.id)
  );

  return {
    unitById,
    roots,
    width: maxX + H_PAD,
    height: maxY + V_PAD,
    orphans,
    orphanUnitIds,
  };
}


// Minimap overview lives in its own file now (Sep 2026 refactor kickoff)
// so the family-tree page stops growing without bound. Only the parent
// still knows about layout math + selection state — the minimap itself
// is a pure presentational component.
// See /app/frontend/src/components/family-tree/TreeMinimap.jsx

function TreeView({ members, rels, tree, isAdmin, onChanged }) {
  const treeSlug = tree?.slug || tree?.id;
  const collapseKey = `ft-tree-collapsed-${treeSlug || "unknown"}`;

  // Full-screen mode — tree canvas fills the browser window (Esc exits).
  const [fullscreen, setFullscreen] = useState(false);
  const [popupMember, setPopupMember] = useState(null);
  const [searchQ, setSearchQ] = useState("");
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e) => { if (e.key === "Escape") setFullscreen(false); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [fullscreen]);

  // Persist collapsed unit IDs per tree in localStorage.
  const [collapsedIds, setCollapsedIds] = useState(() => {
    try {
      const raw = localStorage.getItem(collapseKey);
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  });
  // Quick-edit drawer: when non-null, renders the right-side edit panel
  // for that member without navigating away from the tree canvas.
  const [quickEditId, setQuickEditId] = useState(null);
  // Ancestor Highlight — when non-null, everything except the lineage
  // (ancestors + descendants) of this unit is dimmed to 0.15 opacity so
  // the visitor can read a single branch clearly. Cleared by clicking
  // canvas background or the "Show all" pill.
  const [focusedUnitId, setFocusedUnitId] = useState(null);
  // When the visitor navigates to a different tree, reload the collapsed
  // set for that tree so state doesn't bleed across pages.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(collapseKey);
      setCollapsedIds(raw ? new Set(JSON.parse(raw)) : new Set());
    } catch { setCollapsedIds(new Set()); }
  }, [collapseKey]);
  useEffect(() => {
    try { localStorage.setItem(collapseKey, JSON.stringify([...collapsedIds])); } catch { /* quota */ }
  }, [collapseKey, collapsedIds]);

  const toggleCollapse = useCallback((uid) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  }, []);

  const layout = useMemo(() => buildFamilyLayout(members, rels, collapsedIds), [members, rels, collapsedIds]);
  const memberById = useMemo(() => {
    const m = new Map();
    members.forEach((x) => m.set(x.id, x));
    return m;
  }, [members]);

  // Ancestor Highlight — set of unit IDs to keep fully opaque when
  // `focusedUnitId` is set. Includes: the focused unit itself, every
  // ancestor unit walking up via `parentUnitId`, AND every descendant
  // via `children`. Everything else fades to 0.15 opacity.
  const focusedUnitIds = useMemo(() => {
    if (!focusedUnitId) return null;
    const keep = new Set();
    // Walk up ancestors
    let cur = focusedUnitId;
    while (cur) {
      if (keep.has(cur)) break;
      keep.add(cur);
      const u = layout.unitById.get(cur);
      cur = u ? u.parentUnitId : null;
    }
    // Walk down descendants (BFS)
    const stack = [focusedUnitId];
    while (stack.length) {
      const uid = stack.pop();
      const u = layout.unitById.get(uid);
      if (!u) continue;
      for (const cid of u.children) {
        if (!keep.has(cid)) {
          keep.add(cid);
          stack.push(cid);
        }
      }
    }
    return keep;
  }, [focusedUnitId, layout.unitById]);
  // Reset focus when the underlying members change (e.g. viewer navigates trees).
  useEffect(() => { setFocusedUnitId(null); }, [treeSlug]);
  const dimStyle = (uid) => (
    focusedUnitIds && !focusedUnitIds.has(uid)
      ? { opacity: 0.14, transition: "opacity 240ms ease" }
      : { opacity: 1, transition: "opacity 240ms ease" }
  );

  // Collapse/Expand all — every unit that has children.
  const collapseAll = () => {
    const s = new Set();
    for (const u of layout.unitById.values()) {
      if (u.children.length > 0 && !layout.orphanUnitIds.has(u.id)) s.add(u.id);
    }
    setCollapsedIds(s);
  };
  const expandAll = () => setCollapsedIds(new Set());

  const [scale, setScale] = useState(1.0);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef({ active: false, sx: 0, sy: 0, tx0: 0, ty0: 0, moved: false });
  const canvasRef = useRef(null);
  // True once the visitor has manually panned, zoomed, or fit — we then
  // stop the auto-center effect from re-firing on layout re-computations
  // (which happen on browser-zoom, sidebar toggle, member add, etc.).
  const hasUserInteractedRef = useRef(false);
  const markUserInteracted = () => { hasUserInteractedRef.current = true; };

  const zoomIn  = () => { markUserInteracted(); setScale((s) => Math.min(+(s + 0.15).toFixed(2), 2.5)); };
  const zoomOut = () => { markUserInteracted(); setScale((s) => Math.max(+(s - 0.15).toFixed(2), 0.15)); };
  const zoomFit = useCallback(() => {
    markUserInteracted();
    const box = canvasRef.current?.getBoundingClientRect();
    if (!box || !layout.width || !layout.height) return;
    const sx = (box.width - 20) / layout.width;
    const sy = (box.height - 20) / layout.height;
    const s = Math.max(0.15, Math.min(1.2, Math.min(sx, sy)));
    setScale(s);
    setTx(Math.max(0, (box.width - layout.width * s) / 2));
    setTy(10);
  }, [layout.width, layout.height]);
  const zoomReset = () => { hasUserInteractedRef.current = false; centerOnFirstRoot(); markUserInteracted(); };

  // Center on the primary root couple at 100% zoom. Ensures the tree
  // opens on a legible, readable snapshot instead of a distant "fit-all"
  // that shows nothing but tiny dots. Falls back to Fit if there's no
  // room at 100%.
  const centerOnFirstRoot = useCallback(() => {
    const box = canvasRef.current?.getBoundingClientRect();
    const firstRootId = layout.roots.find((r) => !layout.orphanUnitIds.has(r));
    const firstRoot = firstRootId ? layout.unitById.get(firstRootId) : null;
    if (!box || !firstRoot) return;
    const s = 1.0;
    setScale(s);
    // Place the root couple horizontally centred, near the top.
    const cx = firstRoot.x + firstRoot.w / 2;
    setTx(box.width / 2 - cx * s);
    setTy(24);
  }, [layout.roots, layout.unitById, layout.orphanUnitIds]);

  // Auto-center ONCE on first render — ref guard prevents the effect
  // from stomping on the user's pan/zoom every time the layout changes
  // (browser-zoom, viewport resize, sidebar toggle, add-member, etc.).
  const didAutoCenterRef = useRef(false);
  useEffect(() => {
    if (didAutoCenterRef.current) return;
    if (hasUserInteractedRef.current) return;
    const t = setTimeout(() => {
      centerOnFirstRoot();
      didAutoCenterRef.current = true;
    }, 50);
    return () => clearTimeout(t);
  }, [centerOnFirstRoot]);

  const onMouseDown = (e) => {
    // Ignore drags that start on a card — let the link handle click.
    if (e.target.closest("a[data-testid^='tree-node-']")) return;
    dragRef.current = { active: true, sx: e.clientX, sy: e.clientY, tx0: tx, ty0: ty, moved: false };
    setDragging(true);
    markUserInteracted();
  };
  const onMouseMove = (e) => {
    if (!dragRef.current.active) return;
    const dx = e.clientX - dragRef.current.sx;
    const dy = e.clientY - dragRef.current.sy;
    if (Math.abs(dx) + Math.abs(dy) > 3) dragRef.current.moved = true;
    setTx(dragRef.current.tx0 + dx);
    setTy(dragRef.current.ty0 + dy);
  };
  const onMouseUp = () => { dragRef.current.active = false; setDragging(false); };

  // ── Touch gestures — one-finger pan + two-finger pinch-zoom.
  // The canvas already sets `touchAction: "none"` so the browser never
  // hijacks these gestures for page scroll / native zoom.
  const pinchRef = useRef(null);
  const onTouchStart = (e) => {
    markUserInteracted();
    if (e.touches.length === 2) {
      const [a, b] = [e.touches[0], e.touches[1]];
      pinchRef.current = {
        dist: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY),
        scale0: scale, tx0: tx, ty0: ty,
      };
      dragRef.current.active = false;
      setDragging(false);
    } else if (e.touches.length === 1) {
      const t = e.touches[0];
      dragRef.current = { active: true, sx: t.clientX, sy: t.clientY, tx0: tx, ty0: ty, moved: false };
      setDragging(true);
    }
  };
  const onTouchMove = (e) => {
    if (pinchRef.current && e.touches.length === 2) {
      const [a, b] = [e.touches[0], e.touches[1]];
      const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      const p = pinchRef.current;
      const next = Math.max(0.15, Math.min(2.5, p.scale0 * (dist / p.dist)));
      const box = canvasRef.current?.getBoundingClientRect();
      const cx = (a.clientX + b.clientX) / 2 - (box?.left || 0);
      const cy = (a.clientY + b.clientY) / 2 - (box?.top || 0);
      const k = next / p.scale0;
      setScale(next);
      setTx(cx - k * (cx - p.tx0));
      setTy(cy - k * (cy - p.ty0));
    } else if (dragRef.current.active && e.touches.length === 1) {
      const t = e.touches[0];
      const dx = t.clientX - dragRef.current.sx;
      const dy = t.clientY - dragRef.current.sy;
      if (Math.abs(dx) + Math.abs(dy) > 3) dragRef.current.moved = true;
      setTx(dragRef.current.tx0 + dx);
      setTy(dragRef.current.ty0 + dy);
    }
  };
  const onTouchEnd = (e) => {
    if (e.touches.length < 2) pinchRef.current = null;
    if (e.touches.length === 0) { dragRef.current.active = false; setDragging(false); }
  };

  // Minimap needs the live canvas size to draw the viewport rectangle.
  const [canvasBox, setCanvasBox] = useState(null);
  useEffect(() => {
    const el = canvasRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const measure = () => setCanvasBox({ width: el.clientWidth, height: el.clientHeight });
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    measure();
    return () => ro.disconnect();
  }, [members.length]);
  const miniNavigate = useCallback((wx, wy) => {
    markUserInteracted();
    const el = canvasRef.current;
    if (!el) return;
    setTx(el.clientWidth / 2 - wx * scale);
    setTy(el.clientHeight / 2 - wy * scale);
  }, [scale]);

  const onWheel = (e) => {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    markUserInteracted();
    const box = canvasRef.current?.getBoundingClientRect();
    const cx = box ? e.clientX - box.left : 0;
    const cy = box ? e.clientY - box.top : 0;
    setScale((s) => {
      const next = Math.max(0.15, Math.min(2.5, s * (e.deltaY < 0 ? 1.12 : 1 / 1.12)));
      // Zoom around cursor.
      const k = next / s;
      setTx((t) => cx - k * (cx - t));
      setTy((t) => cy - k * (cy - t));
      return next;
    });
  };

  if (members.length === 0) {
    return (
      <div className="wofy-card text-center py-16">
        <Users className="w-6 h-6 mx-auto mb-2" style={{ color: "var(--wofy-primary)" }} />
        <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>No members yet. Add your first family member to start the tree.</p>
      </div>
    );
  }

  // Build SVG connector paths — smooth Bezier curves from parent card
  // bottom-center to each child card top-center. Two vertical stubs join
  // at a horizontal bus mid-way through the generation gap, then curve
  // outward into the child. Each connector remembers its parent+child
  // unit IDs so Ancestor Highlight can dim non-focused lines.
  const connectors = [];
  const maxDepth = Math.max(0, ...Array.from(layout.unitById.values()).map((u) => (u.y != null ? Math.round(u.y / ROW_H) : 0)));
  for (const u of layout.unitById.values()) {
    if (!u.children.length) continue;
    if (layout.orphanUnitIds.has(u.id)) continue;
    if (collapsedIds.has(u.id)) continue;
    if (u.x == null || u.y == null) continue;
    const parentBottomX = u.x + u.w / 2;
    const parentBottomY = u.y + TREE_CARD_H;
    const busY = parentBottomY + GEN_GAP / 2;
    const childCenters = u.children
      .map((cid) => ({ cid, c: layout.unitById.get(cid) }))
      .filter(({ c }) => c && c.x != null && c.y != null)
      .map(({ cid, c }) => ({ cid, x: c.x + c.w / 2, y: c.y }));
    if (!childCenters.length) continue;
    for (const child of childCenters) {
      // A cubic Bezier: (parentBottom → busY straight, then curve to child)
      // Control points are placed on the bus line to give a smooth ogee
      // that feels like heirloom penmanship rather than an org-chart.
      const d = [
        `M ${parentBottomX} ${parentBottomY}`,
        `C ${parentBottomX} ${busY} ${child.x} ${busY} ${child.x} ${child.y}`,
      ].join(" ");
      const focused = focusedUnitIds && focusedUnitIds.has(u.id) && focusedUnitIds.has(child.cid);
      const dim = focusedUnitIds && !focused;
      connectors.push(
        <path
          key={`conn-${u.id}-${child.cid}`}
          d={d}
          opacity={dim ? 0.1 : 0.92}
          strokeWidth={focused ? 2.2 : 1.6}
        />
      );
    }
  }

  // Person search + MyHeritage-style member popup (plain computations —
  // no hooks here because an early return sits above in this component).
  const searchResults = (() => {
    const q = searchQ.trim().toLowerCase();
    if (q.length < 2) return [];
    return members.filter((m) => {
      const nameHit = [m.first_name, m.middle_name, m.last_name, m.nickname]
        .filter(Boolean).join(" ").toLowerCase().includes(q);
      const locHit = [m.birth_place, m.current_location, m.location, m.origin_location]
        .filter(Boolean).join(" ").toLowerCase().includes(q);
      return nameHit || locHit;
    }).slice(0, 12);
  })();

  const highlightMember = (mid) => {
    const locate = () => {
      const el = document.querySelector(`[data-testid="tree-node-${mid}"]`);
      if (!el) return false;
      el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
      const prev = el.style.boxShadow;
      el.style.boxShadow = "0 0 0 4px rgba(194,65,12,0.55), 0 12px 32px -8px rgba(194,65,12,0.45)";
      el.style.transition = "box-shadow 350ms ease";
      setTimeout(() => { el.style.boxShadow = prev; }, 3000);
      return true;
    };
    if (!locate()) { expandAll(); setTimeout(locate, 400); }
  };

  const treeViewContent = (
    <div
      data-testid="family-tree-view"
      className={fullscreen ? "fixed inset-0 z-[999] p-3 overflow-y-auto" : ""}
      style={fullscreen ? { background: "#FBF5E9" } : undefined}
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="text-[10px] font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
          {fullscreen && <strong className="mr-2 normal-case tracking-normal font-display text-sm" style={{ color: "var(--wofy-text)" }}>{tree?.name}</strong>}
          {layout.unitById.size - layout.orphanUnitIds.size} unit{layout.unitById.size - layout.orphanUnitIds.size === 1 ? "" : "s"} · {members.length} people · {layout.roots.filter((r) => !layout.orphanUnitIds.has(r)).length} root branch{layout.roots.filter((r) => !layout.orphanUnitIds.has(r)).length === 1 ? "" : "es"}{collapsedIds.size > 0 ? ` · ${collapsedIds.size} collapsed` : ""}
        </div>
        <div className="inline-flex items-center gap-1 flex-wrap">
          <div className="relative">
            <input
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              placeholder="Search by name or place…"
              className="rounded-full border px-3 py-1 text-xs w-44 focus:w-64 transition-all outline-none"
              style={{ borderColor: "var(--wofy-border)" }}
              data-testid="tree-person-search"
            />
            {searchResults.length > 0 && (
              <div className="absolute left-0 top-8 z-20 w-64 rounded-xl border bg-white shadow-xl overflow-hidden" style={{ borderColor: "var(--wofy-border)" }} data-testid="tree-person-search-results">
                {searchResults.map((m) => {
                  const loc = m.current_location || m.birth_place || m.location || "";
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => { setSearchQ(""); highlightMember(m.id); setPopupMember(m); }}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-orange-50 flex items-center gap-2"
                      data-testid={`tree-person-result-${m.id}`}
                    >
                      <AncestorAvatar member={m} size={24} showAddChip={false} className="!rounded-full shrink-0" />
                      <span className="flex-1 min-w-0">
                        <span className="block truncate">{[m.first_name, m.last_name].filter(Boolean).join(" ")}</span>
                        {loc && <span className="block text-[10px] opacity-60 truncate">📍 {loc}</span>}
                      </span>
                      {m.birth_date && <span className="opacity-50 ml-1">{m.birth_date.slice(0, 4)}</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <span className="w-px h-4 mx-1" style={{ background: "var(--wofy-line)" }} aria-hidden />
          <button type="button" onClick={expandAll}   className="wofy-btn-ghost text-xs px-2" data-testid="tree-expand-all"   aria-label="Expand all branches" disabled={collapsedIds.size === 0}>Expand all</button>
          <button type="button" onClick={collapseAll} className="wofy-btn-ghost text-xs px-2" data-testid="tree-collapse-all" aria-label="Collapse all branches">Collapse all</button>
          <span className="w-px h-4 mx-1" style={{ background: "var(--wofy-line)" }} aria-hidden />
          <button type="button" onClick={zoomOut} className="wofy-btn-ghost text-xs px-2" data-testid="tree-zoom-out" aria-label="Zoom out">−</button>
          <span className="text-[10px] font-mono tabular-nums px-1 min-w-[3.5rem] text-center" data-testid="tree-zoom-value">{Math.round(scale * 100)}%</span>
          <button type="button" onClick={zoomIn} className="wofy-btn-ghost text-xs px-2" data-testid="tree-zoom-in" aria-label="Zoom in">+</button>
          <button type="button" onClick={zoomFit} className="wofy-btn-ghost text-xs px-2" data-testid="tree-zoom-fit" aria-label="Fit to screen">Fit</button>
          <button type="button" onClick={zoomReset} className="wofy-btn-ghost text-xs px-2" data-testid="tree-zoom-reset" aria-label="Reset zoom">Reset</button>
          <span className="w-px h-4 mx-1" style={{ background: "var(--wofy-line)" }} aria-hidden />
          {fullscreen ? (
            <>
              <button type="button" onClick={() => setFullscreen(false)} className="wofy-btn-ghost text-xs px-2 inline-flex items-center gap-1" data-testid="tree-minimize" aria-label="Minimize tree view" title="Back to page view">
                <Minimize2 className="w-3.5 h-3.5" /> Minimize
              </button>
              <button type="button" onClick={() => setFullscreen(false)} className="rounded-full p-1.5 text-white" style={{ background: "#B25D38" }} data-testid="tree-fullscreen-close" aria-label="Close full screen" title="Close (Esc)">
                <X className="w-3.5 h-3.5" />
              </button>
            </>
          ) : (
            <button type="button" onClick={() => setFullscreen(true)} className="rounded-full px-3 py-1 text-xs font-semibold text-white inline-flex items-center gap-1.5" style={{ background: "#B25D38" }} data-testid="tree-fullscreen" aria-label="View tree full screen" title="Full screen">
              <Maximize2 className="w-3.5 h-3.5" /> Full screen
            </button>
          )}
        </div>
      </div>

      <div
        ref={canvasRef}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        onWheel={onWheel}
        onClick={(e) => {
          // Clicking blank canvas (not a card) clears the ancestor-highlight focus.
          if (focusedUnitId && !dragRef.current.moved) {
            const el = e.target;
            if (!el.closest || !el.closest('[data-testid^="family-unit-"]')) {
              setFocusedUnitId(null);
            }
          }
        }}
        className="relative border rounded-lg overflow-hidden select-none"
        style={{
          borderColor: "rgba(160,124,60,0.4)",
          height: fullscreen ? "calc(100vh - 84px)" : "78vh",
          cursor: dragging ? "grabbing" : "grab",
          touchAction: "none",
          // Parchment vignette — warm ivory center fading to soft cream
          // at the edges. A faint grid of gold dots gives the paper a
          // subtle "atlas" feel without competing with the cards.
          background: `
            radial-gradient(ellipse at center, #FDF8EC 0%, #F5EBD1 90%),
            radial-gradient(circle, rgba(160,124,60,0.10) 1px, transparent 1px)
          `,
          backgroundSize: "100% 100%, 28px 28px",
          backgroundBlendMode: "multiply, normal",
        }}
        data-testid="tree-canvas"
      >
        {/* Ancestor Highlight — floating "Show all" pill visible when focused */}
        {focusedUnitId && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setFocusedUnitId(null); }}
            className="absolute top-3 left-3 z-20 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold shadow-md border transition hover:scale-105"
            style={{
              background: "#FEFCF7",
              borderColor: "#B58936",
              color: "#3B2E1F",
            }}
            data-testid="tree-focus-clear"
            aria-label="Clear ancestor highlight and show all branches"
          >
            <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden><path d="M1 1L9 9M9 1L1 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
            Show all branches
          </button>
        )}
        <div
          className="absolute top-0 left-0 origin-top-left"
          style={{
            transform: `translate3d(${tx}px, ${ty}px, 0) scale(${scale})`,
            width: layout.width,
            height: layout.height,
          }}
          data-testid="tree-inner"
        >
          {/* Generation rail — Roman-numeral labels down the left gutter,
              one per row of the tree. Anchored inside the transformed
              inner layer so it pans+zooms with the canvas. */}
          <div
            className="absolute top-0 pointer-events-none"
            style={{ left: -2, width: 42, height: layout.height }}
            aria-hidden
          >
            {Array.from({ length: maxDepth + 1 }).map((_, d) => {
              const y = d * ROW_H + TREE_CARD_H / 2 - 12;
              const roman = ["I","II","III","IV","V","VI","VII","VIII","IX","X","XI","XII","XIII","XIV","XV"][d] || (d + 1);
              return (
                <div
                  key={`gen-${d}`}
                  className="absolute -left-1 flex items-center gap-1"
                  style={{ top: y, color: "#8A6A2E", opacity: 0.75 }}
                >
                  <span
                    className="font-display italic text-xs tracking-wider"
                    style={{ fontFamily: "'Playfair Display', Georgia, serif", textShadow: "0 1px 0 rgba(255,255,255,0.6)" }}
                  >
                    {roman}.
                  </span>
                  <span className="h-px w-4" style={{ background: "linear-gradient(90deg, rgba(160,124,60,0.55), transparent)" }} />
                </div>
              );
            })}
          </div>

          <svg
            width={layout.width}
            height={layout.height}
            style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none" }}
            aria-hidden
          >
            <g stroke="#B58936" strokeLinecap="round" strokeLinejoin="round" fill="none">
              {connectors}
            </g>
          </svg>

          {Array.from(layout.unitById.values()).map((u) => {
            if (layout.orphanUnitIds.has(u.id)) return null;
            if (u.x == null || u.y == null) return null;   // hidden inside a collapsed subtree
            const a = memberById.get(u.members[0]);
            const b = u.members[1] ? memberById.get(u.members[1]) : null;
            const hasKids = u.children.length > 0;
            const isCollapsed = collapsedIds.has(u.id);
            const focusStyle = dimStyle(u.id);
            const isFocused = focusedUnitId === u.id;
            return (
              <div
                key={u.id}
                style={{
                  position: "absolute",
                  left: u.x,
                  top: u.y,
                  width: u.w,
                  height: TREE_CARD_H,
                  display: "flex",
                  alignItems: "stretch",
                  ...focusStyle,
                  // Ring around the focused unit for extra clarity.
                  outline: isFocused ? "2px solid #B58936" : "none",
                  outlineOffset: isFocused ? 4 : 0,
                  borderRadius: isFocused ? 16 : 0,
                }}
                data-testid={`family-unit-${u.id}`}
                onClickCapture={(e) => {
                  // Ancestor Highlight — only trigger on a genuine click,
                  // not the end of a pan-drag. We swallow the click so the
                  // inner Link<Profile> doesn't fire on the first tap.
                  if (dragRef.current.moved) return;
                  if (focusedUnitId === u.id) return;
                  // Ignore clicks on the collapse/quick-edit/copy-link controls.
                  const el = e.target;
                  if (el && el.closest && (
                    el.closest('[data-testid^="family-unit-toggle-"]') ||
                    el.closest('[data-testid^="tree-node-quickedit-"]') ||
                    el.closest('[data-testid^="tree-node-copy-"]')
                  )) return;
                  e.preventDefault();
                  e.stopPropagation();
                  setFocusedUnitId(u.id);
                }}
              >
                {a && <TreeMiniCard m={a} treeSlug={treeSlug} deceased={!!a.death_date} isAdmin={isAdmin} onQuickEdit={() => setQuickEditId(a.id)} onOpen={setPopupMember} generation={(u.depth ?? 0) + 1} />}
                {b && (
                  <>
                    <div style={{ alignSelf: "center", width: COUPLE_GAP, height: 2, background: "#B58936", opacity: 0.6 }} aria-hidden />
                    <TreeMiniCard m={b} treeSlug={treeSlug} deceased={!!b.death_date} isAdmin={isAdmin} onQuickEdit={() => setQuickEditId(b.id)} onOpen={setPopupMember} generation={(u.depth ?? 0) + 1} />
                  </>
                )}
                {hasKids && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); e.preventDefault(); toggleCollapse(u.id); }}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="absolute rounded-full shadow-sm border bg-white hover:bg-purple-50 transition-colors inline-flex items-center justify-center gap-0.5 px-1.5"
                    style={{
                      left: u.w / 2 - (isCollapsed ? 22 : 11),
                      bottom: -12,
                      height: 22,
                      minWidth: 22,
                      borderColor: "var(--wofy-primary)",
                      color: "var(--wofy-primary)",
                      fontSize: 10,
                      lineHeight: 1,
                      zIndex: 2,
                    }}
                    data-testid={`family-unit-toggle-${u.id}`}
                    aria-label={isCollapsed ? `Expand ${u.descendantCount} descendants` : "Collapse this branch"}
                    title={isCollapsed ? `Show ${u.descendantCount} descendant${u.descendantCount === 1 ? "" : "s"}` : "Hide this branch"}
                  >
                    {isCollapsed ? (
                      <>
                        <ChevronDown className="w-3 h-3" strokeWidth={2.5} />
                        <span className="font-mono font-semibold">+{u.descendantCount}</span>
                      </>
                    ) : (
                      <ChevronUp className="w-3 h-3" strokeWidth={2.5} />
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {members.length >= 1 && (
          <TreeMinimap
            layout={layout}
            tx={tx} ty={ty} scale={scale}
            canvasBox={canvasBox}
            onNavigate={miniNavigate}
            highlightId={popupMember?.id || null}
            cardHeight={TREE_CARD_H}
          />
        )}
      </div>

      {layout.orphans.length > 0 && (
        <div className="mt-4 pt-3 border-t" style={{ borderColor: "var(--wofy-line)" }} data-testid="tree-orphans">
          <div className="text-[10px] font-mono uppercase tracking-wider mb-2" style={{ color: "var(--wofy-text-soft)" }}>
            Not yet linked · {layout.orphans.length}
            {isAdmin && <span className="ml-2 normal-case opacity-60">— click the trash icon to remove, or the pencil to quick-edit</span>}
          </div>
          <div className="flex gap-2 flex-wrap">
            {layout.orphans.map((id) => {
              const m = memberById.get(id);
              if (!m) return null;
              return (
                <OrphanCard
                  key={id}
                  m={m}
                  treeSlug={treeSlug}
                  treeId={tree?.id}
                  isAdmin={!!isAdmin}
                  onChanged={onChanged}
                  onQuickEdit={() => setQuickEditId(id)}
                  onOpen={(mm) => setPopupMember(mm)}
                />
              );
            })}
          </div>
        </div>
      )}

      <p className="text-[10px] mt-2 opacity-60">
        Drag with a finger or mouse to pan · Pinch or Ctrl/⌘ + scroll to zoom (or use +/−) · Click the chevron under any couple to collapse or expand that branch · Tap a card to open the profile.
      </p>

      {popupMember && (
        <MemberPopup
          m={popupMember}
          treeSlug={treeSlug}
          treeId={tree?.id}
          isAdmin={isAdmin}
          onClose={() => setPopupMember(null)}
          onLocate={() => highlightMember(popupMember.id)}
          onEdit={isAdmin ? () => { setQuickEditId(popupMember.id); setPopupMember(null); } : null}
        />
      )}

      {quickEditId && (
        <QuickEditDrawer
          memberId={quickEditId}
          tree={tree}
          onClose={() => setQuickEditId(null)}
          onSaved={() => { if (typeof onChanged === "function") onChanged(); }}
        />
      )}
    </div>
  );
  return fullscreen ? createPortal(treeViewContent, document.body) : treeViewContent;
}

// Legacy card kept for future use — no longer rendered in the tree canvas.
// eslint-disable-next-line no-unused-vars
function MemberCard({ m, treeId, treeSlug, user, tree }) {
  const name = [m.first_name, m.middle_name, m.last_name].filter(Boolean).join(" ");
  const dateStr = (d, yearOnly) => {
    if (!d) return "";
    if (yearOnly) return d.slice(0, 4);
    if (d.length === 4) return d;
    return d.slice(0, 4);
  };
  const b = dateStr(m.birth_date, m.birth_date_is_year_only);
  const dth = dateStr(m.death_date, m.death_date_is_year_only);
  const dates = [b, dth].filter(Boolean).join(" – ");
  const deceased = !!m.death_date;
  return (
    <div
      data-testid={`family-member-${m.id}`}
      className="w-44 rounded-xl border p-2 text-center relative"
      style={{
        borderColor: "var(--wofy-border)",
        background: deceased ? "rgba(139,92,246,0.05)" : "var(--wofy-paper)",
      }}
    >
      <Link
        to={`/family/${treeSlug || treeId}/member/${m.id}`}
        className="block group"
        title="Open full profile"
        data-testid={`family-member-open-${m.id}`}
      >
        <div className="w-24 h-24 mx-auto rounded-full overflow-hidden mb-1.5 border-2 group-hover:scale-105 transition-transform relative" style={{ borderColor: deceased ? "#5B4A73" : "var(--wofy-primary)" }}>
          {m.photo ? (
            <img src={m.photo} alt="" className="w-full h-full object-cover" />
          ) : (
            <InitialsAvatar
              name={name}
              gender={m.gender}
              fill
              className="w-full h-full"
            />
          )}
          <CopyLinkIcon
            url={m.memorial_slug ? `/celebrate/${m.memorial_slug}` : `/family/${treeSlug || treeId}/member/${m.id}`}
            position="tr"
            label={m.memorial_slug ? "Copy memorial link" : "Copy profile link"}
            testId={`family-member-copy-${m.id}`}
          />
        </div>
        <div className="font-semibold text-sm leading-snug group-hover:underline">{name}</div>
        {m.nickname && (
          <div className="text-[11px] italic leading-tight" style={{ color: "var(--wofy-primary)" }} data-testid={`family-member-nickname-${m.id}`} title="Nickname">
            "{m.nickname}"
          </div>
        )}
        {dates && <div className="text-[10px] font-mono" style={{ color: "var(--wofy-text-soft)" }}>{dates}</div>}
        {m.location && !deceased && (
          <div className="text-[10px] leading-tight mt-0.5 inline-flex items-center gap-0.5 justify-center max-w-full" style={{ color: "var(--wofy-text-soft)" }} data-testid={`family-member-location-${m.id}`} title="Current place">
            <MapPin className="w-2.5 h-2.5 flex-none opacity-60" />
            <span className="truncate">{m.location}</span>
          </div>
        )}
      </Link>

      {/* Memorial CTA — deceased members */}
      {deceased && (
        <MemorialCta member={m} />
      )}

      {/* Claim for Edit — every registered user can request */}
      {user && (
        <ClaimForEditButton treeId={treeId} member={m} />
      )}

      {/* Contact this member (Admin/Member badge) — every logged-in user */}
      {user && (
        <ContactMemberButton treeId={treeId} member={m} tree={tree} />
      )}
    </div>
  );
}

function ContactMemberButton({ treeId, member, tree }) {
  const [open, setOpen] = useState(false);
  const uid = member.claimed_by_user_id;
  const isTreeAdmin = tree && (uid === (tree.owner_id) || (tree.admin_ids || []).includes(uid));
  const role = isTreeAdmin ? "admin" : "member";
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="mt-1.5 text-[10px] inline-flex items-center gap-1 rounded-full px-2 py-0.5 border w-full justify-center"
        style={{ borderColor: "var(--wofy-border)", color: "#2563eb" }}
        data-testid={`family-member-contact-${member.id}`}
      >
        <MessageCircle className="w-3 h-3" />
        {role === "admin" ? "Admin · Contact" : "Member · Contact"}
      </button>
      {open && (
        <FamilyMemberContactModal
          treeId={treeId}
          member={member}
          role={role}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function MemorialCta({ member }) {
  if (member.memorial_slug) {
    return (
      <Link
        to={`/celebrate/${member.memorial_slug}`}
        className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold underline"
        style={{ color: "#5B4A73" }}
        data-testid={`family-member-memorial-${member.id}`}
      >
        <Flower2 className="w-3 h-3" /> Visit Memorial
      </Link>
    );
  }
  const name = [member.first_name, member.middle_name, member.last_name].filter(Boolean).join(" ");
  const qs = new URLSearchParams({
    from_family_member: member.id || "",
    name: name,
    birth: member.birth_date || "",
    death: member.death_date || "",
    birth_place: member.birth_place || "",
    death_place: member.death_place || "",
  }).toString();
  return (
    <Link
      to={`/celebrate/submit?${qs}`}
      className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold rounded-full px-2 py-0.5"
      style={{ background: "#5B4A73", color: "white" }}
      data-testid={`family-member-create-memorial-${member.id}`}
    >
      <Flower2 className="w-3 h-3" /> Create Memorial
    </Link>
  );
}

function ClaimForEditButton({ treeId, member }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [rel, setRel] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | pending | already
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await http.post(`/family-trees/${treeId}/members/${member.id}/claim`, {
        note: note.trim() || undefined,
        relationship: rel.trim() || undefined,
      });
      const s = data.status;
      if (s === "already_editor") { toast.success("You already have edit access."); setStatus("already"); }
      else if (s === "already_pending") { toast.info("Your claim is already pending."); setStatus("pending"); }
      else { toast.success("Claim sent. The owner will review it."); setStatus("pending"); }
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send claim");
    } finally { setBusy(false); }
  };
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        disabled={status === "pending" || status === "already"}
        className="mt-1.5 text-[10px] inline-flex items-center gap-1 rounded-full px-2 py-0.5 border w-full justify-center disabled:opacity-60"
        style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-primary)" }}
        data-testid={`family-member-claim-${member.id}`}
      >
        <UserPlus className="w-3 h-3" />
        {status === "pending" ? "Claim pending" : status === "already" ? "Editor ✓" : "Claim for Edit"}
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-[#14213D]/25 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form onSubmit={submit} className="wofy-card w-full max-w-md text-left" onClick={(e) => e.stopPropagation()} data-testid={`claim-modal-${member.id}`}>
            <div className="overline mb-2">CLAIM FOR EDIT</div>
            <h3 className="font-display text-xl mb-1">
              Request to edit <span className="italic">{[member.first_name, member.last_name].filter(Boolean).join(" ")}</span>
            </h3>
            <p className="text-xs mb-3" style={{ color: "var(--wofy-text-soft)" }}>
              The tree owner will review your request. If approved, you'll be able to add photos, correct details, and enrich this member's profile.
            </p>
            <input
              className="wofy-input mb-2"
              placeholder="Your relationship (e.g. grandson, cousin, close friend)"
              value={rel}
              onChange={(e) => setRel(e.target.value)}
              maxLength={120}
              data-testid={`claim-rel-${member.id}`}
            />
            <textarea
              className="wofy-input"
              placeholder="Optional note — why you'd like to edit, what you can contribute…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              maxLength={500}
              data-testid={`claim-note-${member.id}`}
            />
            <div className="flex justify-end gap-2 mt-3">
              <button type="button" onClick={() => setOpen(false)} className="wofy-btn-ghost text-sm">Cancel</button>
              <button type="submit" disabled={busy} className="wofy-btn-primary text-sm" data-testid={`claim-submit-${member.id}`}>
                {busy ? "Sending…" : "Send claim"}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Members list (table).                                              */
/* ------------------------------------------------------------------ */
function MembersList({ members, isAdmin, treeId, treeSlug, tree, onDeleted, user }) {
  const [openComments, setOpenComments] = useState(null);
  const [contactMember, setContactMember] = useState(null); // opens the visitor contact form
  const del = async (id) => {
    if (!window.confirm("Remove this member? Their relationships will also be removed.")) return;
    try {
      await http.delete(`/family-trees/${treeId}/members/${id}`);
      toast.success("Member removed");
      onDeleted?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const fmtDate = (d, yearOnly) => {
    if (!d) return "";
    if (yearOnly || d.length === 4) return d.slice(0, 4);
    return d;
  };

  // Map member.user_id → tree role so cards can badge Admin/Editor.
  const memberRole = useCallback((m) => {
    if (!m?.user_id || !tree) return null;
    if (tree.owner_id === m.user_id) return "Admin";
    if ((tree.admin_user_ids || []).includes(m.user_id)) return "Admin";
    if ((tree.editor_user_ids || []).includes(m.user_id)) return "Editor";
    return null;
  }, [tree]);

  // Public trees or private trees you have access to → card is a link
  // to the full profile. Private without access → link is disabled.
  const isPublic = (tree?.visibility || "public") === "public";
  const canOpenProfile = isPublic || isAdmin || (user && (tree?.member_user_ids || []).includes(user.id));

  if (members.length === 0) return <div className="wofy-card text-sm text-center" style={{ color: "var(--wofy-text-soft)" }}>No members yet.</div>;
  return (
    <div>
      <TreeAdminsStrip tree={tree} treeSlug={treeSlug} />
      <div className="grid md:grid-cols-2 gap-3" data-testid="ft-members-grid">
      {members.map((m) => {
        const name = [m.first_name, m.middle_name, m.last_name].filter(Boolean).join(" ");
        const b = fmtDate(m.birth_date, m.birth_date_is_year_only);
        const dth = fmtDate(m.death_date, m.death_date_is_year_only);
        const dateLine = [b, dth].filter(Boolean).join(" – ") || "Dates unknown";
        const role = memberRole(m);
        const profileHref = canOpenProfile ? `/family/${treeSlug}/member/${m.id}` : null;

        const cardInner = (
          <>
            <div className="w-14 h-14 rounded-full overflow-hidden shrink-0 border" style={{ borderColor: "var(--wofy-border)" }}>
              {m.photo ? <img src={m.photo} alt="" className="w-full h-full object-cover" /> : <InitialsAvatar name={name} gender={m.gender} fill className="w-full h-full" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-semibold">{name}</div>
                {role && (
                  <span
                    className="px-1.5 py-0.5 rounded text-[10px] font-mono uppercase"
                    style={{ background: role === "Admin" ? "#DBEAFE" : "#FEF3C7", color: role === "Admin" ? "#1E40AF" : "#92400E" }}
                    data-testid={`ft-member-role-${m.id}`}
                  >
                    {role}
                  </span>
                )}
              </div>
              {m.other_names && (
                <div className="text-xs italic" style={{ color: "var(--wofy-text-soft)" }}>
                  Also known as: {pickI18n(m.other_names)}
                </div>
              )}
              <div className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>
                {dateLine}{m.birth_place && ` · ${m.birth_place}`}
              </div>
              {m.bio && <p className="text-xs mt-1 line-clamp-2">{pickI18n(m.bio)}</p>}
              <div className="flex items-center gap-3 mt-2" onClick={(e) => e.stopPropagation()}>
                {m.memorial_slug && (
                  <Link to={`/celebrate/${m.memorial_slug}`} className="text-xs underline inline-flex items-center gap-0.5" style={{ color: "#5B4A73" }}>
                    <Flower2 className="w-3 h-3" /> Memorial
                  </Link>
                )}
                {user && (
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpenComments(openComments === m.id ? null : m.id); }}
                    data-testid={`ft-comments-toggle-${m.id}`}
                    className="text-xs inline-flex items-center gap-0.5 hover:underline"
                    style={{ color: "var(--wofy-primary)" }}
                  >
                    <MessageCircle className="w-3 h-3" /> {openComments === m.id ? "Hide" : "Comments"}
                  </button>
                )}
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setContactMember(m); }}
                  className="text-xs inline-flex items-center gap-0.5 hover:underline font-medium ml-auto"
                  style={{ color: "var(--wofy-primary)" }}
                  data-testid={`ft-member-contact-${m.id}`}
                >
                  Contact &raquo;
                </button>
              </div>
              {openComments === m.id && (
                <MemberComments treeId={treeId} memberId={m.id} user={user} />
              )}
            </div>
            {isAdmin && (
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); del(m.id); }}
                className="opacity-50 hover:opacity-100"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" style={{ color: "#B23A3A" }} />
              </button>
            )}
          </>
        );

        return profileHref ? (
          <Link
            key={m.id}
            to={profileHref}
            className="wofy-card flex items-start gap-3 hover:shadow-md transition"
            data-testid={`ft-member-card-${m.id}`}
          >
            {cardInner}
          </Link>
        ) : (
          <div
            key={m.id}
            className="wofy-card flex items-start gap-3 cursor-not-allowed"
            title="This tree is private — request access to see the full profile"
            data-testid={`ft-member-card-${m.id}`}
          >
            {cardInner}
          </div>
        );
      })}
      {contactMember && (
        <MemberContactModal
          treeSlug={treeSlug}
          member={contactMember}
          onClose={() => setContactMember(null)}
        />
      )}
      </div>
    </div>
  );
}


/* ------------------------------------------------------------------ */
/* Admins strip — persistent visible list of tree Admins & Editors    */
/* with a click-to-contact button so visitors always know who to      */
/* reach for access requests, corrections, or new information.        */
/* ------------------------------------------------------------------ */
function TreeAdminsStrip({ tree, treeSlug }) {
  const [state, setState] = useState({ loading: true, items: [] });
  const [openModal, setOpenModal] = useState(null); // {kind:'admin', user:{name,handle,id}}

  useEffect(() => {
    if (!tree?.id) return;
    http.get(`/family-trees/${tree.id}/roles`)
      .then((r) => {
        const d = r.data || {};
        // Endpoint returns {owner, admins:[], editors:[]}. Treat the
        // owner as an Admin so the strip is never empty for trees that
        // only have a solo owner.
        const owner = d.owner ? [{ ...d.owner, role: "admin" }] : [];
        const admins = [...owner, ...(d.admins || [])];
        const editors = d.editors || [];
        setState({ loading: false, items: [...admins, ...editors] });
      })
      .catch(() => setState({ loading: false, items: [] }));
  }, [tree?.id]);

  const admins  = (state.items || []).filter((r) => (r.role || "").toLowerCase() === "admin" || (r.role || "").toLowerCase() === "owner");
  const editors = (state.items || []).filter((r) => (r.role || "").toLowerCase() === "editor");

  if (state.loading || (admins.length === 0 && editors.length === 0)) return null;

  return (
    <div
      className="mb-4 rounded-xl border p-3"
      style={{ borderColor: "var(--wofy-border)", background: "linear-gradient(135deg,#EFF6FF 0%,#FEF3C7 100%)" }}
      data-testid="ft-admins-strip"
    >
      <div className="text-xs font-mono uppercase mb-2 opacity-70">
        Tree admins &amp; editors — contact them to add yourself or request access
      </div>
      <div className="flex flex-wrap gap-2">
        {admins.map((r) => (
          <button
            key={`admin-${r.id || r.user_id}`}
            type="button"
            onClick={() => setOpenModal({ user_name: r.name || r.email || r.handle, role: "Admin" })}
            className="inline-flex items-center gap-2 rounded-full border pl-1 pr-3 py-1 bg-white hover:shadow-sm"
            style={{ borderColor: "var(--wofy-border)" }}
            data-testid={`ft-admin-strip-${r.id || r.user_id}`}
          >
            {(r.avatar_url || r.profile_picture || r.photo)
              ? <img src={resolveImage(r.avatar_url || r.profile_picture || r.photo)} alt="" className="w-6 h-6 rounded-full object-cover" />
              : <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-800 text-xs flex items-center justify-center">
                  {(r.name || r.email || r.handle || "?")[0]}
                </span>}
            <span className="text-xs font-medium">{r.name || r.email || r.handle || "Admin"}</span>
            <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded" style={{ background: "#DBEAFE", color: "#1E40AF" }}>
              Admin
            </span>
          </button>
        ))}
        {editors.map((r) => (
          <button
            key={`editor-${r.id || r.user_id}`}
            type="button"
            onClick={() => setOpenModal({ user_name: r.name || r.email || r.handle, role: "Editor" })}
            className="inline-flex items-center gap-2 rounded-full border pl-1 pr-3 py-1 bg-white hover:shadow-sm"
            style={{ borderColor: "var(--wofy-border)" }}
            data-testid={`ft-editor-strip-${r.id || r.user_id}`}
          >
            {(r.avatar_url || r.profile_picture || r.photo)
              ? <img src={resolveImage(r.avatar_url || r.profile_picture || r.photo)} alt="" className="w-6 h-6 rounded-full object-cover" />
              : <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-800 text-xs flex items-center justify-center">
                  {(r.name || r.email || r.handle || "?")[0]}
                </span>}
            <span className="text-xs font-medium">{r.name || r.email || r.handle || "Editor"}</span>
            <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded" style={{ background: "#FEF3C7", color: "#92400E" }}>
              Editor
            </span>
          </button>
        ))}
      </div>
      {openModal && (
        <TreeContactModal
          treeSlug={treeSlug}
          treeName={tree?.name}
          adminName={openModal.user_name}
          role={openModal.role}
          onClose={() => setOpenModal(null)}
        />
      )}
    </div>
  );
}

/* Lightweight modal that posts to the tree's owner via the shared
 * `member_contact_requests` endpoint (using the tree owner as the
 * "member" so admins receive an email + inbox notification).        */
function TreeContactModal({ treeSlug, treeName, adminName, role, onClose }) {
  const [yourName, setYourName] = useState("");
  const [yourEmail, setYourEmail] = useState("");
  const [subject, setSubject] = useState("add_me");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!yourName.trim() || !yourEmail.trim() || !message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setBusy(true);
    try {
      await http.post(`/family-trees/${treeSlug}/tree-contact`, {
        your_name: yourName.trim(),
        your_email: yourEmail.trim(),
        subject,
        message: message.trim(),
      });
      setSent(true);
      toast.success("Message delivered to the tree admins.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send the message");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose} data-testid="ft-tree-contact-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="overline text-xs">CONTACT REQUEST</div>
            <h3 className="font-display text-xl mt-1">
              Reach {adminName} ({role}) about {treeName}
            </h3>
            <p className="text-xs opacity-70 mt-1">
              Your message goes to all tree admins. They'll reply directly to your email.
            </p>
          </div>
          <button onClick={onClose} className="opacity-50 hover:opacity-100 text-2xl leading-none">×</button>
        </div>
        {sent ? (
          <div className="mt-6 rounded-lg border p-4 text-sm" style={{ borderColor: "var(--wofy-border)", background: "#F0FDF4", color: "#166534" }} data-testid="ft-tree-contact-success">
            Thanks! Your message has been delivered. Admins will reply to <b>{yourEmail}</b>.
            <div className="mt-3">
              <button onClick={onClose} className="wofy-btn-ghost text-sm">Close</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input value={yourName} onChange={(e) => setYourName(e.target.value)} placeholder="Your name" className="rounded-lg border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--wofy-border)" }} data-testid="ft-tree-contact-name" required />
              <input type="email" value={yourEmail} onChange={(e) => setYourEmail(e.target.value)} placeholder="Your email" className="rounded-lg border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--wofy-border)" }} data-testid="ft-tree-contact-email" required />
            </div>
            <select value={subject} onChange={(e) => setSubject(e.target.value)} className="rounded-lg border px-3 py-2 text-sm w-full outline-none bg-white" style={{ borderColor: "var(--wofy-border)" }} data-testid="ft-tree-contact-subject">
              <option value="add_me">Please add me to this family tree</option>
              <option value="need_access">Requesting access</option>
              <option value="share_info">I have information / photos to share</option>
              <option value="correction">Correction or update needed</option>
              <option value="other">General enquiry</option>
            </select>
            <textarea rows={5} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Add details — how you're related, what you'd like to share, or the update needed." className="rounded-lg border px-3 py-2 text-sm w-full outline-none" style={{ borderColor: "var(--wofy-border)" }} data-testid="ft-tree-contact-message" required />
            <div className="flex items-center justify-end gap-2">
              <button type="button" onClick={onClose} className="wofy-btn-ghost text-sm">Cancel</button>
              <button type="submit" disabled={busy} className="wofy-btn-primary text-sm" data-testid="ft-tree-contact-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {busy ? "Sending…" : "Send message"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}


/* ------------------------------------------------------------------ */
/* Visitor contact modal — reaches the tree admin(s) by email + inbox */
/* ------------------------------------------------------------------ */
function MemberContactModal({ treeSlug, member, onClose }) {
  const memberName = [member.first_name, member.middle_name, member.last_name].filter(Boolean).join(" ") || "this member";
  const [yourName, setYourName] = useState("");
  const [yourEmail, setYourEmail] = useState("");
  const [subject, setSubject] = useState("add_me");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!yourName.trim() || !yourEmail.trim() || !message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setBusy(true);
    try {
      await http.post(`/family-trees/${treeSlug}/members/${member.id}/contact`, {
        your_name: yourName.trim(),
        your_email: yourEmail.trim(),
        subject,
        message: message.trim(),
      });
      setSent(true);
      toast.success("Message sent to the tree admins.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send the message");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
      onClick={onClose}
      data-testid="ft-contact-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="overline text-xs">CONTACT REQUEST</div>
            <h3 className="font-display text-xl mt-1">Reach the admins about {memberName}</h3>
            <p className="text-xs opacity-70 mt-1">
              Your message goes to the tree admins — not the member directly.
              They'll reply to your email.
            </p>
          </div>
          <button onClick={onClose} className="opacity-50 hover:opacity-100 text-2xl leading-none" data-testid="ft-contact-close">×</button>
        </div>

        {sent ? (
          <div className="mt-6 rounded-lg border p-4 text-sm" style={{ borderColor: "var(--wofy-border)", background: "#F0FDF4", color: "#166534" }} data-testid="ft-contact-success">
            Thanks! Your message has been delivered. The tree admins will get back to you at{" "}
            <b>{yourEmail}</b>.
            <div className="mt-3">
              <button onClick={onClose} className="wofy-btn-ghost text-sm">Close</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input
                value={yourName}
                onChange={(e) => setYourName(e.target.value)}
                placeholder="Your name"
                className="rounded-lg border px-3 py-2 text-sm outline-none"
                style={{ borderColor: "var(--wofy-border)" }}
                data-testid="ft-contact-name"
                required
              />
              <input
                type="email"
                value={yourEmail}
                onChange={(e) => setYourEmail(e.target.value)}
                placeholder="Your email"
                className="rounded-lg border px-3 py-2 text-sm outline-none"
                style={{ borderColor: "var(--wofy-border)" }}
                data-testid="ft-contact-email"
                required
              />
            </div>
            <select
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="rounded-lg border px-3 py-2 text-sm w-full outline-none bg-white"
              style={{ borderColor: "var(--wofy-border)" }}
              data-testid="ft-contact-subject"
            >
              <option value="add_me">Please add me to this family tree</option>
              <option value="need_access">Requesting access to view the full profile</option>
              <option value="share_info">I have information / photos to share</option>
              <option value="correction">Correction or update needed</option>
              <option value="other">General enquiry</option>
            </select>
            <textarea
              rows={5}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Add details — how you're related, what you'd like to share, or the update needed."
              className="rounded-lg border px-3 py-2 text-sm w-full outline-none"
              style={{ borderColor: "var(--wofy-border)" }}
              data-testid="ft-contact-message"
              required
            />
            <div className="flex items-center justify-end gap-2">
              <button type="button" onClick={onClose} className="wofy-btn-ghost text-sm">Cancel</button>
              <button
                type="submit"
                disabled={busy}
                className="wofy-btn-primary text-sm"
                data-testid="ft-contact-submit"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {busy ? "Sending…" : "Send message"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Comments per member                                                */
/* ------------------------------------------------------------------ */
function MemberComments({ treeId, memberId, user }) {
  const [items, setItems] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const { data } = await http.get(`/family-trees/${treeId}/members/${memberId}/comments`);
      setItems(data.items);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [memberId]);
  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try {
      await http.post(`/family-trees/${treeId}/members/${memberId}/comments`, { body: body.trim() });
      setBody("");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to post"); }
    finally { setBusy(false); }
  };
  const del = async (cid) => {
    if (!window.confirm("Delete this comment?")) return;
    try { await http.delete(`/family-trees/${treeId}/comments/${cid}`); load(); }
    catch (e) { toast.error("Failed"); }
  };
  return (
    <div className="mt-3 pt-3 border-t space-y-2" style={{ borderColor: "var(--wofy-line)" }} data-testid={`ft-comments-${memberId}`}>
      {items.length === 0 && <div className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>No comments yet — be the first to share a memory.</div>}
      {items.map((c) => (
        <div key={c.id} className="text-xs">
          <div className="flex items-baseline justify-between gap-2">
            <div className="font-semibold">{c.author_name}</div>
            {c.author_id === user?.id && (
              <button onClick={() => del(c.id)} className="opacity-40 hover:opacity-100"><Trash2 className="w-3 h-3" /></button>
            )}
          </div>
          <p style={{ color: "var(--wofy-text-soft)" }}>{c.body}</p>
        </div>
      ))}
      {user && (
        <form onSubmit={submit} className="flex gap-1 pt-1">
          <input
            data-testid={`ft-comment-input-${memberId}`}
            className="wofy-input flex-1 text-xs"
            placeholder="Share a memory or note…"
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <button data-testid={`ft-comment-submit-${memberId}`} type="submit" disabled={busy} className="wofy-btn-primary !py-1 !px-3"><Send className="w-3 h-3" /></button>
        </form>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Events tab.                                                        */
/* ------------------------------------------------------------------ */
function EventsList({ events, treeId, onChanged, isAdmin }) {
  const [activeCat, setActiveCat] = useState("all");
  const rsvp = async (id, response) => {
    try {
      await http.post(`/family-trees/${treeId}/events/${id}/rsvp`, { response });
      toast.success(`RSVP: ${response}`);
      onChanged?.();
    } catch (e) { toast.error("Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try { await http.delete(`/family-trees/${treeId}/events/${id}`); toast.success("Deleted"); onChanged?.(); }
    catch (e) { toast.error("Failed"); }
  };
  const notify = async (id) => {
    try {
      const { data } = await http.post(`/family-trees/${treeId}/events/${id}/notify`);
      toast.success(`Reminder sent to ${data.sent_to} member(s).`);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send"); }
  };
  if (events.length === 0) return <div className="wofy-card text-sm text-center" style={{ color: "var(--wofy-text-soft)" }}>No family events yet.</div>;

  // Only show category chips that have at least one event.
  const cats = categoriesWithEvents(events);
  const filtered = activeCat === "all"
    ? events
    : events.filter((e) => getEventMeta(e.event_type)?.categoryId === activeCat);

  return (
    <div className="space-y-3">
      {/* Category filter tabs — only categories with posted events. */}
      {cats.length > 1 && (
        <div className="flex gap-2 flex-wrap" data-testid="ft-events-categories">
          <button
            type="button"
            onClick={() => setActiveCat("all")}
            data-testid="ft-events-cat-all"
            className="text-xs px-3 py-1 rounded-full border inline-flex items-center gap-1.5"
            style={{
              borderColor: activeCat === "all" ? "var(--wofy-primary)" : "var(--wofy-border)",
              background: activeCat === "all" ? "var(--wofy-primary)" : "transparent",
              color: activeCat === "all" ? "white" : "var(--wofy-text)",
            }}
          >
            All <span className="opacity-70 font-mono">{events.length}</span>
          </button>
          {cats.map((c) => {
            const count = events.filter((e) => getEventMeta(e.event_type)?.categoryId === c.id).length;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setActiveCat(c.id)}
                data-testid={`ft-events-cat-${c.id}`}
                className="text-xs px-3 py-1 rounded-full border inline-flex items-center gap-1.5"
                style={{
                  borderColor: activeCat === c.id ? "var(--wofy-primary)" : "var(--wofy-border)",
                  background: activeCat === c.id ? "var(--wofy-primary)" : "transparent",
                  color: activeCat === c.id ? "white" : "var(--wofy-text)",
                }}
              >
                <span aria-hidden>{c.icon}</span>
                <span>{c.label}</span>
                <span className="opacity-70 font-mono">{count}</span>
              </button>
            );
          })}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-3">
      {filtered.map((e) => {
        const meta = getEventMeta(e.event_type);
        return (
        <div key={e.id} className="wofy-card" data-testid={`ft-event-card-${e.id}`}>
          <div className="flex items-baseline justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-wider font-mono inline-flex items-center gap-1.5" style={{ color: "var(--wofy-primary)" }}>
                <span aria-hidden className="text-base leading-none">{meta.icon}</span>
                <span>{meta.label}</span>
                <span className="opacity-60">·</span>
                <span className="opacity-70">{meta.categoryLabel}</span>
              </div>
              <h3 className="font-display text-lg mt-0.5">{e.title}</h3>
              <div className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>
                {new Date(e.event_date).toLocaleDateString(undefined, { dateStyle: "full" })}
                {e.location && ` · ${e.location}`}
              </div>
            </div>
            {isAdmin && <button onClick={() => del(e.id)} className="opacity-50 hover:opacity-100"><Trash2 className="w-4 h-4" style={{ color: "#B23A3A" }} /></button>}
          </div>
          {e.description && <p className="mt-2 text-sm" style={{ color: "var(--wofy-text-soft)" }}>{e.description}</p>}
          <div className="mt-3 text-xs flex items-center gap-3">
            <span style={{ color: "var(--wofy-text-soft)" }}>
              Going: <b>{e.rsvp_summary?.yes || 0}</b> · Maybe: {e.rsvp_summary?.maybe || 0} · Not: {e.rsvp_summary?.no || 0}
            </span>
          </div>
          <div className="mt-2 flex gap-2 flex-wrap items-center">
            {["yes", "maybe", "no"].map((r) => (
              <button
                key={r}
                data-testid={`ft-rsvp-${e.id}-${r}`}
                onClick={() => rsvp(e.id, r)}
                className={`text-xs px-3 py-1 rounded-full border`}
                style={{
                  borderColor: e.rsvp_summary?.my_response === r ? "var(--wofy-primary)" : "var(--wofy-border)",
                  background: e.rsvp_summary?.my_response === r ? "var(--wofy-primary)" : "transparent",
                  color: e.rsvp_summary?.my_response === r ? "white" : "var(--wofy-text)",
                }}
              >
                {r === "yes" ? "Going" : r === "maybe" ? "Maybe" : "Can't make it"}
              </button>
            ))}
            {isAdmin && (
              <button
                onClick={() => notify(e.id)}
                data-testid={`ft-notify-${e.id}`}
                className="text-xs px-3 py-1 rounded-full border ml-auto inline-flex items-center gap-1"
                style={{ borderColor: "var(--wofy-primary)", color: "var(--wofy-primary)" }}
                title="Email a reminder to every tree member"
              >
                <Send className="w-3 h-3" /> Notify members
              </button>
            )}
          </div>
        </div>
        );
      })}
    </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Albums tab (Phase 2).                                              */
/* ------------------------------------------------------------------ */
function AlbumsTab({ treeId, isAdmin }) {
  const [albums, setAlbums] = useState([]);
  const [creating, setCreating] = useState(false);
  const [openAlbum, setOpenAlbum] = useState(null);
  const load = async () => {
    try {
      const { data } = await http.get(`/family-trees/${treeId}/albums`);
      setAlbums(data.items);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [treeId]);
  const del = async (aid) => {
    if (!window.confirm("Delete this album and every photo inside?")) return;
    try { await http.delete(`/family-trees/${treeId}/albums/${aid}`); toast.success("Album deleted"); load(); }
    catch (e) { toast.error("Failed"); }
  };
  return (
    <div data-testid="ft-albums-tab">
      {isAdmin && (
        <div className="flex justify-end mb-3">
          <button onClick={() => setCreating(true)} className="wofy-btn-primary" data-testid="ft-album-new">
            <Plus className="w-4 h-4" /> New album
          </button>
        </div>
      )}
      {albums.length === 0 ? (
        <div className="wofy-card text-sm text-center" style={{ color: "var(--wofy-text-soft)" }}>
          No albums yet. Photo albums are a warm way to gather milestones — weddings, reunions, family holidays.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {albums.map((a) => (
            <div key={a.id} className="wofy-card cursor-pointer" onClick={() => setOpenAlbum(a)} data-testid={`ft-album-${a.id}`}>
              {a.cover_photo ? (
                <img src={resolveImage(a.cover_photo)} alt="" className="w-full h-40 object-cover rounded-lg mb-3" />
              ) : (
                <div className="w-full h-40 rounded-lg mb-3 flex items-center justify-center" style={{ background: "var(--wofy-surface)" }}>
                  <ImageIcon className="w-8 h-8" style={{ color: "var(--wofy-text-soft)" }} />
                </div>
              )}
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-display text-lg leading-snug">{a.title}</div>
                  {a.description && <p className="text-xs mt-1 line-clamp-2" style={{ color: "var(--wofy-text-soft)" }}>{a.description}</p>}
                  <div className="mt-2 text-xs" style={{ color: "var(--wofy-text-soft)" }}>{a.photo_count || 0} photo{a.photo_count === 1 ? "" : "s"}</div>
                </div>
                {isAdmin && (
                  <button onClick={(e) => { e.stopPropagation(); del(a.id); }} className="opacity-50 hover:opacity-100"><Trash2 className="w-4 h-4" style={{ color: "#B23A3A" }} /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {creating && <NewAlbumModal treeId={treeId} onClose={() => setCreating(false)} onCreated={load} />}
      {openAlbum && <AlbumViewer treeId={treeId} album={openAlbum} onClose={() => { setOpenAlbum(null); load(); }} />}
    </div>
  );
}

function NewAlbumModal({ treeId, onClose, onCreated }) {
  const [f, setF] = useState({ title: "", description: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.title.trim()) return toast.error("Give the album a title");
    setBusy(true);
    try { await http.post(`/family-trees/${treeId}/albums`, f); toast.success("Album created"); onClose(); onCreated?.(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-[#14213D]/25 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="wofy-card w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="ft-album-modal">
        <div className="overline mb-2">NEW ALBUM</div>
        <form onSubmit={submit} className="space-y-3">
          <input data-testid="ft-album-title" className="wofy-input" placeholder="Album title *" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required />
          <textarea className="wofy-input" placeholder="Description (optional)" rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="wofy-btn-ghost">Cancel</button>
            <button type="submit" disabled={busy} data-testid="ft-album-create" className="wofy-btn-primary">{busy ? "Creating…" : "Create album"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AlbumViewer({ treeId, album, onClose }) {
  const [photos, setPhotos] = useState([]);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const { data } = await http.get(`/family-trees/${treeId}/albums/${album.id}/photos`);
      setPhotos(data.items);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [album.id]);

  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 3 * 1024 * 1024) return toast.error("Photo must be < 3 MB");
    const dataUrl = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });
    setBusy(true);
    try {
      await http.post(`/family-trees/${treeId}/albums/${album.id}/photos`, { image: dataUrl, caption });
      toast.success("Photo added");
      setCaption("");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const del = async (pid) => {
    if (!window.confirm("Delete this photo?")) return;
    try { await http.delete(`/family-trees/${treeId}/albums/${album.id}/photos/${pid}`); load(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#14213D]/30 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="wofy-card w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="ft-album-viewer">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="overline">ALBUM</div>
            <h3 className="font-display text-2xl">{album.title}</h3>
            {album.description && <p className="text-sm mt-1" style={{ color: "var(--wofy-text-soft)" }}>{album.description}</p>}
          </div>
          <button onClick={onClose} className="opacity-60 hover:opacity-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <input
            className="wofy-input flex-1 text-sm"
            placeholder="Caption (optional)"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
          />
          <label className="wofy-btn-primary cursor-pointer" data-testid="ft-album-upload">
            <Plus className="w-4 h-4" /> {busy ? "Uploading…" : "Add photo"}
            <input type="file" accept="image/*" className="hidden" onChange={onFile} disabled={busy} />
          </label>
        </div>
        {photos.length === 0 ? (
          <div className="text-sm text-center py-8" style={{ color: "var(--wofy-text-soft)" }}>No photos yet — add the first memory.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {photos.map((p) => (
              <div key={p.id} className="relative group" data-testid={`ft-photo-${p.id}`}>
                <img src={resolveImage(p.image)} alt={p.caption || ""} className="w-full aspect-square object-cover rounded-lg" />
                {p.caption && <div className="text-xs mt-1 line-clamp-2" style={{ color: "var(--wofy-text-soft)" }}>{p.caption}</div>}
                <button onClick={() => del(p.id)} className="absolute top-1 right-1 p-1 rounded-full bg-white/85 text-[#14213D] border border-[#14213D]/20 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Trash2 className="w-3 h-3 text-white" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Modals.                                                            */
/* ------------------------------------------------------------------ */
// ============================================================
// DateBlock — MUST be declared at module scope, NOT inside a parent
// component. Redeclaring it on every render was making React remount
// the <input> on every keystroke → focus loss + only 1 char accepted.
// ============================================================
function DateBlock({ label, mode, setMode, value, onValueChange, testid }) {
  return (
    <div className="rounded-lg border p-3" style={{ borderColor: "var(--wofy-border)" }}>
      <div className="text-xs font-mono uppercase tracking-wider mb-2" style={{ color: "var(--wofy-text-soft)" }}>{label}</div>
      <div className="flex gap-1 mb-2">
        {[["date", "Full date"], ["year", "Year only"], ["unknown", "Unknown"]].map(([v, l]) => (
          <button
            key={v}
            type="button"
            data-testid={`${testid}-mode-${v}`}
            onClick={() => setMode(v)}
            className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full border"
            style={{
              borderColor: mode === v ? "var(--wofy-primary)" : "var(--wofy-border)",
              background: mode === v ? "var(--wofy-primary)" : "transparent",
              color: mode === v ? "white" : "var(--wofy-text-soft)",
            }}
          >{l}</button>
        ))}
      </div>
      {mode === "date" && (
        <input type="date" data-testid={`${testid}-date`} className="wofy-input" value={value} onChange={(e) => onValueChange(e.target.value)} />
      )}
      {mode === "year" && (
        <input
          type="text"
          inputMode="numeric"
          data-testid={`${testid}-year`}
          className="wofy-input"
          placeholder="e.g. 1952"
          maxLength={4}
          value={value}
          onChange={(e) => onValueChange(e.target.value.replace(/\D/g, "").slice(0, 4))}
        />
      )}
      {mode === "unknown" && (
        <div className="text-xs italic" style={{ color: "var(--wofy-text-soft)" }}>Will be recorded as unknown.</div>
      )}
    </div>
  );
}

function MemberModal({ treeId, members = [], onClose, onSaved }) {
  const draftKey = `ft-draft-member-${treeId}`;
  // Restore draft (once) so accidental navigation / refresh doesn't wipe input.
  const initial = (() => {
    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return null;
  })();
  const [f, setF] = useState(initial?.f || {
    first_name: "", middle_name: "", last_name: "", nickname: "", other_names: "",
    gender: "", birth_date: "", birth_date_is_year_only: false,
    death_date: "", death_date_is_year_only: false,
    birth_place: "", current_location: "", bio: "", photo: "",
  });
  const [busy, setBusy] = useState(false);
  const [birthMode, setBirthMode] = useState(initial?.birthMode || "date"); // "date" | "year" | "unknown"
  const [deathMode, setDeathMode] = useState(initial?.deathMode || "unknown"); // "date" | "year" | "unknown"
  // Placement — how the new person connects to an existing member.
  // relation: "" (unlinked / new root) | "parent" | "child" | "spouse" | "sibling"
  const [placement, setPlacement] = useState({ relation: "", anchor_id: "", generation_override: "" });
  const [anchorQuery, setAnchorQuery] = useState("");
  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const updP = (k, v) => setPlacement((p) => ({ ...p, [k]: v }));

  const anchorResults = useMemo(() => {
    const s = anchorQuery.trim().toLowerCase();
    if (!s) return (members || []).slice(0, 10);
    return (members || []).filter((m) => {
      const nm = [m.first_name, m.middle_name, m.last_name, m.nickname].filter(Boolean).join(" ").toLowerCase();
      const loc = [m.birth_place, m.current_location, m.location].filter(Boolean).join(" ").toLowerCase();
      return nm.includes(s) || loc.includes(s);
    }).slice(0, 10);
  }, [anchorQuery, members]);
  const anchorMember = useMemo(
    () => (members || []).find((m) => m.id === placement.anchor_id) || null,
    [members, placement.anchor_id]
  );

  // Auto-save draft on every change (skip photo — too large for localStorage).
  useEffect(() => {
    try {
      const { photo, ...rest } = f;
      const hasContent = Object.values(rest).some((v) => typeof v === "string" ? v.trim() : v);
      if (hasContent) {
        localStorage.setItem(draftKey, JSON.stringify({ f: rest, birthMode, deathMode }));
      }
    } catch { /* ignore quota */ }
  }, [f, birthMode, deathMode, draftKey]);

  const clearDraft = () => { try { localStorage.removeItem(draftKey); } catch { /* ignore */ } };
  const hasDraft = !!initial;

  const onPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error("Photo must be < 2 MB");
    const dataUrl = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });
    upd("photo", dataUrl);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.first_name.trim()) return toast.error("First name is required");
    // Normalise birth/death based on mode
    const payload = { ...f };
    if (birthMode === "unknown") { payload.birth_date = ""; payload.birth_date_is_year_only = false; }
    else if (birthMode === "year") { payload.birth_date_is_year_only = true; }
    else { payload.birth_date_is_year_only = false; }
    if (deathMode === "unknown") { payload.death_date = ""; payload.death_date_is_year_only = false; }
    else if (deathMode === "year") { payload.death_date_is_year_only = true; }
    else { payload.death_date_is_year_only = false; }
    // Validate year values
    const yearOk = (v) => /^\d{4}$/.test((v || "").trim());
    if (birthMode === "year" && payload.birth_date && !yearOk(payload.birth_date)) return toast.error("Birth year must be a 4-digit year");
    if (deathMode === "year" && payload.death_date && !yearOk(payload.death_date)) return toast.error("Death year must be a 4-digit year");
    setBusy(true);
    try {
      // Include current_location + generation_override
      if (placement.generation_override !== "" && placement.generation_override != null) {
        payload.generation_override = Number(placement.generation_override);
      }
      const { data } = await http.post(`/family-trees/${treeId}/members`, payload);
      const newMid = data?.id || data?.member_id;
      // Wire up the relationship if the admin chose one.
      if (newMid && placement.relation && placement.anchor_id) {
        try {
          if (placement.relation === "parent") {
            // New person is the PARENT of the anchor.
            await http.post(`/family-trees/${treeId}/relationships`, {
              person1_id: newMid, person2_id: placement.anchor_id, type: "parent",
            });
          } else if (placement.relation === "child") {
            // New person is a CHILD of the anchor.
            await http.post(`/family-trees/${treeId}/relationships`, {
              person1_id: placement.anchor_id, person2_id: newMid, type: "parent",
            });
          } else if (placement.relation === "spouse") {
            await http.post(`/family-trees/${treeId}/relationships`, {
              person1_id: placement.anchor_id, person2_id: newMid, type: "spouse",
            });
          } else if (placement.relation === "sibling") {
            // Sibling = share a parent with the anchor. If the anchor
            // already has parents, link the new person to those same
            // parents. Otherwise fall back to a plain sibling relation.
            const relsResp = await http.get(`/family-trees/${treeId}/relationships`);
            const anchorParents = (relsResp.data?.items || [])
              .filter((r) => r.type === "parent" && r.person2_id === placement.anchor_id)
              .map((r) => r.person1_id);
            if (anchorParents.length) {
              for (const pid of anchorParents) {
                await http.post(`/family-trees/${treeId}/relationships`, {
                  person1_id: pid, person2_id: newMid, type: "parent",
                });
              }
            } else {
              await http.post(`/family-trees/${treeId}/relationships`, {
                person1_id: placement.anchor_id, person2_id: newMid, type: "sibling",
              });
            }
          }
        } catch (linkErr) {
          toast.error(linkErr?.response?.data?.detail || "Member added, but linking failed.");
        }
      }
      if (data.memorial_slug) toast.success("Member added + memorial draft created.");
      else toast.success("Member added");
      if (data.limit_warning) {
        const { used, limit, plan_name } = data.limit_warning;
        window.dispatchEvent(new CustomEvent("ift:upgrade-required", {
          detail: { message: `Your tree is almost full — ${used} of ${limit} members used on the ${plan_name} plan (${Math.round((used / limit) * 100)}%). Upgrade now to keep growing without interruption.` },
        }));
      }
      clearDraft();
      onClose(); onSaved?.();
    } catch (err) {
      if (err?.response?.status === 402) {
        window.dispatchEvent(new CustomEvent("ift:upgrade-required", {
          detail: { message: err.response?.data?.detail || "This tree has reached its plan's member limit." },
        }));
      } else toast.error(err?.response?.data?.detail || "Failed");
    }
    finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-[#14213D]/50 backdrop-blur-sm flex items-start justify-center p-4 pt-28 overflow-y-auto"
      onClick={onClose}
    >
      <div className="wofy-card w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="member-modal">
        <div className="overline mb-2">ADD MEMBER</div>
        <h3 className="font-display text-2xl mb-1">Who's joining the tree?</h3>
        {hasDraft && (
          <div className="mb-3 text-[11px] flex items-center justify-between gap-2 px-2 py-1 rounded border" style={{ borderColor: "var(--wofy-border)", background: "rgba(139,92,246,0.06)" }}>
            <span>Draft restored — your previous input was saved locally.</span>
            <button
              type="button"
              onClick={() => {
                clearDraft();
                setF({ first_name: "", middle_name: "", last_name: "", nickname: "", other_names: "", gender: "", birth_date: "", birth_date_is_year_only: false, death_date: "", death_date_is_year_only: false, birth_place: "", current_location: "", bio: "", photo: "" });
                setBirthMode("date"); setDeathMode("unknown");
              }}
              className="underline opacity-80 hover:opacity-100"
              data-testid="mm-clear-draft"
            >Clear draft</button>
          </div>
        )}
        <form onSubmit={submit} className="space-y-3">
          {/* Placement — where does this new person sit in the tree? */}
          {(members && members.length > 0) && (
            <div
              className="rounded-lg border p-3 space-y-2"
              style={{ borderColor: "var(--wofy-border)", background: "rgba(194,65,12,0.04)" }}
              data-testid="mm-placement-panel"
            >
              <div className="text-xs font-semibold" style={{ color: "var(--wofy-primary)" }}>
                Where do they fit in the tree?
              </div>
              <div className="text-[11px] opacity-80 leading-snug">
                Tip: pick an existing family member as the "anchor" and choose how the new person relates to them — parent (adds an older generation), child (adds a younger generation), spouse or sibling. Skip this to add them as an unlinked person you can connect later.
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {[
                  { v: "", label: "Unlinked", hint: "Add later" },
                  { v: "parent", label: "Parent of…", hint: "Older gen" },
                  { v: "child", label: "Child of…", hint: "Younger gen" },
                  { v: "spouse", label: "Spouse of…", hint: "Same gen" },
                  { v: "sibling", label: "Sibling of…", hint: "Same gen" },
                ].map((opt) => (
                  <button
                    key={opt.v || "unlinked"}
                    type="button"
                    onClick={() => updP("relation", opt.v)}
                    data-testid={`mm-placement-${opt.v || "unlinked"}`}
                    className="rounded-lg border px-2 py-1.5 text-xs text-left"
                    style={{
                      borderColor: placement.relation === opt.v ? "var(--wofy-primary)" : "var(--wofy-border)",
                      background: placement.relation === opt.v ? "rgba(194,65,12,0.10)" : "white",
                      color: placement.relation === opt.v ? "var(--wofy-primary)" : "inherit",
                      fontWeight: placement.relation === opt.v ? 600 : 400,
                    }}
                  >
                    <div className="truncate">{opt.label}</div>
                    <div className="text-[10px] opacity-60">{opt.hint}</div>
                  </button>
                ))}
              </div>
              {placement.relation && (
                <div className="space-y-1.5">
                  <label className="text-[10px] font-mono uppercase tracking-wider opacity-70">
                    Anchor person (search by name or place)
                  </label>
                  <input
                    className="wofy-input"
                    placeholder="Start typing a name…"
                    value={anchorMember ? [anchorMember.first_name, anchorMember.last_name].filter(Boolean).join(" ") : anchorQuery}
                    onChange={(e) => { updP("anchor_id", ""); setAnchorQuery(e.target.value); }}
                    data-testid="mm-anchor-search"
                  />
                  {!anchorMember && anchorQuery.trim().length > 0 && (
                    <div className="max-h-40 overflow-y-auto rounded border" style={{ borderColor: "var(--wofy-border)" }}>
                      {anchorResults.length === 0 ? (
                        <div className="px-2 py-1.5 text-[11px] opacity-60">No matching family members.</div>
                      ) : (
                        anchorResults.map((m) => {
                          const nm = [m.first_name, m.last_name].filter(Boolean).join(" ");
                          const loc = m.current_location || m.birth_place || "";
                          return (
                            <button
                              key={m.id}
                              type="button"
                              onClick={() => { updP("anchor_id", m.id); setAnchorQuery(""); }}
                              className="w-full text-left px-2 py-1.5 hover:bg-orange-50 text-xs flex items-center gap-2 border-b last:border-0"
                              style={{ borderColor: "var(--wofy-line)" }}
                              data-testid={`mm-anchor-${m.id}`}
                            >
                              <span className="flex-1 min-w-0">
                                <span className="block truncate">{nm}</span>
                                {loc && <span className="block text-[10px] opacity-60 truncate">📍 {loc}</span>}
                              </span>
                              {(m.birth_date || "").slice(0, 4) && (
                                <span className="opacity-50 text-[10px]">{(m.birth_date || "").slice(0, 4)}</span>
                              )}
                            </button>
                          );
                        })
                      )}
                    </div>
                  )}
                  {anchorMember && (
                    <div className="text-[11px] flex items-center gap-1 opacity-80">
                      Selected: <strong>{[anchorMember.first_name, anchorMember.last_name].filter(Boolean).join(" ")}</strong>
                      <button type="button" onClick={() => { updP("anchor_id", ""); setAnchorQuery(""); }} className="underline opacity-70 hover:opacity-100 ml-1">change</button>
                    </div>
                  )}
                </div>
              )}
              <label className="block text-[11px] mt-1 flex items-center gap-2">
                <span className="font-mono uppercase tracking-wider opacity-70">Generation (optional)</span>
                <span className="opacity-60">G-</span>
                <input
                  type="number" min="1" max="30"
                  value={placement.generation_override}
                  onChange={(e) => updP("generation_override", e.target.value)}
                  placeholder="auto"
                  data-testid="mm-generation-override"
                  className="w-20 px-2 py-1 rounded border text-xs"
                  style={{ borderColor: "var(--wofy-border)" }}
                />
                <span className="text-[10px] opacity-60">Auto by placement — override only if needed.</span>
              </label>
            </div>
          )}


          <div>
            <label className="text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>Full Names</label>
            <div className="grid grid-cols-3 gap-3 mt-1">
              <input data-testid="mm-first-name" className="wofy-input" placeholder="First name *" value={f.first_name} onChange={(e) => upd("first_name", e.target.value)} required />
              <input data-testid="mm-middle-name" className="wofy-input" placeholder="Middle name" value={f.middle_name} onChange={(e) => upd("middle_name", e.target.value)} />
              <input data-testid="mm-last-name" className="wofy-input" placeholder="Last name" value={f.last_name} onChange={(e) => upd("last_name", e.target.value)} />
            </div>
          </div>
          <TranslatableInput
            label="Other Names / Aliases"
            as="input"
            testid="mm-other-names"
            placeholder="e.g. maiden name, nickname, native-script spelling"
            value={f.other_names}
            onChange={(v) => upd("other_names", v)}
            inputCls="!py-2"
          />
          <div className="grid grid-cols-2 gap-3">
            <select data-testid="mm-gender" className="wofy-input" value={f.gender} onChange={(e) => upd("gender", e.target.value)}>
              <option value="">Gender…</option><option value="male">Male</option><option value="female">Female</option><option value="other">Other</option>
            </select>
            <input className="wofy-input" placeholder="Birthplace (optional)" value={f.birth_place} onChange={(e) => upd("birth_place", e.target.value)} />
          </div>
          <input
            className="wofy-input"
            placeholder="Current location — where they live now (optional)"
            value={f.current_location}
            onChange={(e) => upd("current_location", e.target.value)}
            data-testid="mm-current-location"
          />
          <DateBlock label="Date of Birth" mode={birthMode} setMode={setBirthMode} value={f.birth_date} onValueChange={(v) => upd("birth_date", v)} testid="mm-birth" />
          <DateBlock label="Date of Death (leave unknown if living)" mode={deathMode} setMode={setDeathMode} value={f.death_date} onValueChange={(v) => upd("death_date", v)} testid="mm-death" />
          <div>
            <TranslatableInput
              label="Short Bio (optional)"
              as="textarea"
              rows={3}
              testid="mm-bio"
              placeholder="A few lines about them — you can add translations after typing."
              value={f.bio}
              onChange={(v) => upd("bio", v)}
            />
            {typeof f.bio === "string" && f.bio.trim().length >= 10 && (
              <div className="mt-1.5">
                <AIImproveButton
                  value={f.bio}
                  onImproved={(t) => upd("bio", t)}
                  kind="bio"
                  testId="mm-bio-ai"
                />
              </div>
            )}
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>Photo</label>
            <input type="file" accept="image/*" onChange={onPhoto} className="mt-1 block text-xs" data-testid="mm-photo" />
            {f.photo && <img src={f.photo} alt="" className="mt-2 w-20 h-20 rounded-lg object-cover" />}
          </div>
          {deathMode !== "unknown" && f.death_date && (
            <div className="text-xs p-2 rounded border" style={{ borderColor: "#5B4A73", background: "rgba(139,92,246,0.05)", color: "#5B4A73" }}>
              <Flower2 className="w-3 h-3 inline mr-1" /> A draft memorial will be created in Celebrate a Life — you can enrich and publish it later.
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="wofy-btn-ghost">Cancel</button>
            <button type="submit" disabled={busy} data-testid="mm-save" className="wofy-btn-primary">{busy ? "Saving…" : "Add member"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RelationshipModal({ treeId, members, onClose, onSaved }) {
  const [f, setF] = useState({ person1_id: "", person2_id: "", type: "parent", wedding_date: "" });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.person1_id || !f.person2_id || f.person1_id === f.person2_id) return toast.error("Pick two different people");
    setBusy(true);
    try {
      const payload = {
        person1_id: f.person1_id,
        person2_id: f.person2_id,
        type: f.type,
      };
      if (f.type === "spouse" && f.wedding_date) payload.wedding_date = f.wedding_date;
      await http.post(`/family-trees/${treeId}/relationships`, payload);
      toast.success("Linked");
      onClose(); onSaved?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const nm = (m) => [m.first_name, m.last_name].filter(Boolean).join(" ");
  return (
    <div className="fixed inset-0 z-50 bg-[#14213D]/25 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="wofy-card w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="rel-modal">
        <div className="overline mb-2">LINK RELATIONSHIP</div>
        <form onSubmit={submit} className="space-y-3">
          <label className="block text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
            Relationship
            <select className="wofy-input mt-1 normal-case tracking-normal" value={f.type} onChange={(e) => upd("type", e.target.value)}>
              <option value="parent">Parent → Child</option>
              <option value="spouse">Spouse ↔ Spouse</option>
              <option value="sibling">Sibling ↔ Sibling</option>
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
              {f.type === "parent" ? "Parent" : "Person A"}
              <select data-testid="rel-p1" className="wofy-input mt-1 normal-case tracking-normal" value={f.person1_id} onChange={(e) => upd("person1_id", e.target.value)}>
                <option value="">Select…</option>
                {members.map((m) => <option key={m.id} value={m.id}>{nm(m)}</option>)}
              </select>
            </label>
            <label className="block text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
              {f.type === "parent" ? "Child" : "Person B"}
              <select data-testid="rel-p2" className="wofy-input mt-1 normal-case tracking-normal" value={f.person2_id} onChange={(e) => upd("person2_id", e.target.value)}>
                <option value="">Select…</option>
                {members.map((m) => <option key={m.id} value={m.id}>{nm(m)}</option>)}
              </select>
            </label>
          </div>
          {f.type === "spouse" && (
            <label className="block text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
              Wedding date (optional)
              <input
                type="date"
                data-testid="rel-wedding-date"
                className="wofy-input mt-1 normal-case tracking-normal"
                value={f.wedding_date}
                onChange={(e) => upd("wedding_date", e.target.value)}
              />
            </label>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="wofy-btn-ghost">Cancel</button>
            <button type="submit" disabled={busy} className="wofy-btn-primary" data-testid="rel-save">{busy ? "Linking…" : "Link"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EventModal({ treeId, onClose, onSaved }) {
  const [f, setF] = useState({ title: "", event_type: "birthday", event_date: "", location: "", description: "" });
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [activeCat, setActiveCat] = useState(EVENT_CATEGORIES[0].id);
  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.title.trim() || !f.event_date) return toast.error("Title and date required");
    setBusy(true);
    try {
      await http.post(`/family-trees/${treeId}/events`, f);
      toast.success("Event created");
      onClose(); onSaved?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const selectedMeta = getEventMeta(f.event_type);
  return (
    <div className="fixed inset-0 z-50 bg-[#14213D]/25 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="wofy-card w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="event-modal">
        <div className="overline mb-2">NEW FAMILY EVENT</div>
        <form onSubmit={submit} className="space-y-3">
          <input data-testid="ev-title" className="wofy-input" placeholder="Title *" value={f.title} onChange={(e) => upd("title", e.target.value)} required />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <button
                type="button"
                onClick={() => setPickerOpen((v) => !v)}
                data-testid="ev-type-picker"
                className="w-full wofy-input inline-flex items-center justify-between gap-2"
                style={{ textAlign: "left" }}
              >
                <span className="inline-flex items-center gap-2 truncate">
                  <span aria-hidden className="text-lg leading-none">{selectedMeta.icon}</span>
                  <span className="truncate">{selectedMeta.label}</span>
                </span>
                <ChevronDown className="w-4 h-4 shrink-0" />
              </button>
            </div>
            <input type="date" data-testid="ev-date" className="wofy-input" value={f.event_date} onChange={(e) => upd("event_date", e.target.value)} required />
          </div>

          {pickerOpen && (
            <div className="rounded-lg border p-3" style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }} data-testid="ev-type-panel">
              {/* Category tabs */}
              <div className="flex gap-1 flex-wrap mb-3">
                {EVENT_CATEGORIES.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setActiveCat(c.id)}
                    data-testid={`ev-cat-${c.id}`}
                    className="text-xs px-2.5 py-1 rounded-full border inline-flex items-center gap-1"
                    style={{
                      borderColor: activeCat === c.id ? "var(--wofy-primary)" : "var(--wofy-border)",
                      background: activeCat === c.id ? "var(--wofy-primary)" : "transparent",
                      color: activeCat === c.id ? "white" : "var(--wofy-text)",
                    }}
                  >
                    <span aria-hidden>{c.icon}</span>
                    <span>{c.label}</span>
                  </button>
                ))}
              </div>
              {/* Items in the active category */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 max-h-56 overflow-y-auto">
                {EVENT_CATEGORIES.find((c) => c.id === activeCat).items.map((it) => (
                  <button
                    key={it.value}
                    type="button"
                    onClick={() => { upd("event_type", it.value); setPickerOpen(false); }}
                    data-testid={`ev-type-${it.value}`}
                    className="text-left text-xs px-2 py-1.5 rounded border inline-flex items-center gap-2 hover:bg-white"
                    style={{
                      borderColor: f.event_type === it.value ? "var(--wofy-primary)" : "var(--wofy-border)",
                      background: f.event_type === it.value ? "rgba(139,92,246,0.08)" : "transparent",
                    }}
                  >
                    <span aria-hidden className="text-base leading-none">{it.icon}</span>
                    <span className="truncate">{it.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <input className="wofy-input" placeholder="Location (optional)" value={f.location} onChange={(e) => upd("location", e.target.value)} />
          <textarea className="wofy-input" placeholder="Description" rows={3} value={f.description} onChange={(e) => upd("description", e.target.value)} />
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="wofy-btn-ghost">Cancel</button>
            <button type="submit" disabled={busy} data-testid="ev-save" className="wofy-btn-primary">{busy ? "Creating…" : "Create"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Family Chat tab (Phase 3)                                          */
/* ------------------------------------------------------------------ */
function FamilyChatTab({ treeId, user, tree, isAdmin }) {
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const canPost = isAdmin || (user && (tree.member_user_ids || []).includes(user.id));

  const load = async () => {
    try {
      const { data } = await http.get(`/family-trees/${treeId}/messages`);
      setMessages(data.items);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load messages");
    }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [treeId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try {
      await http.post(`/family-trees/${treeId}/messages`, { body: body.trim() });
      setBody("");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const del = async (mid) => {
    if (!window.confirm("Delete this message?")) return;
    try { await http.delete(`/family-trees/${treeId}/messages/${mid}`); load(); }
    catch (e) { toast.error("Failed"); }
  };

  if (!user) return (
    <div className="wofy-card text-sm text-center" style={{ color: "var(--wofy-text-soft)" }}>
      Sign in to view the family chat.
    </div>
  );
  if (error) return (
    <div className="wofy-card text-sm text-center" style={{ color: "var(--wofy-text-soft)" }}>
      {error} <br/>
      <span className="text-xs">Family chat is available to tree admins and members. Ask an admin to invite you.</span>
    </div>
  );

  return (
    <div data-testid="ft-chat-tab" className="wofy-card">
      <div className="max-h-[500px] overflow-y-auto space-y-3 mb-4 pr-2">
        {messages.length === 0 ? (
          <p className="text-sm text-center py-8" style={{ color: "var(--wofy-text-soft)" }}>
            No messages yet — say hello to your family.
          </p>
        ) : messages.map((m) => (
          <div key={m.id} data-testid={`ft-msg-${m.id}`} className={`flex ${m.author_id === user.id ? "justify-end" : "justify-start"}`}>
            <div
              className="max-w-[75%] rounded-2xl px-4 py-2 text-sm"
              style={{
                background: m.author_id === user.id ? "var(--wofy-primary)" : "var(--wofy-surface)",
                color: m.author_id === user.id ? "white" : "var(--wofy-text)",
              }}
            >
              <div className="text-[10px] font-mono uppercase tracking-wider mb-1 opacity-80">
                {m.author_name} · {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </div>
              <p className="whitespace-pre-wrap break-words">{m.body}</p>
              {(m.author_id === user.id || isAdmin) && (
                <button onClick={() => del(m.id)} className="mt-1 text-[10px] opacity-60 hover:opacity-100 underline">delete</button>
              )}
            </div>
          </div>
        ))}
      </div>
      {canPost ? (
        <form onSubmit={submit} className="flex gap-2 pt-3 border-t" style={{ borderColor: "var(--wofy-line)" }}>
          <input
            data-testid="ft-chat-input"
            className="wofy-input flex-1"
            placeholder="Message your family…"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={4000}
          />
          <button type="submit" disabled={busy} data-testid="ft-chat-send" className="wofy-btn-primary">
            <Send className="w-4 h-4" />
          </button>
        </form>
      ) : (
        <div className="text-xs italic text-center pt-3 border-t" style={{ color: "var(--wofy-text-soft)", borderColor: "var(--wofy-line)" }}>
          Only tree members can post. Ask an admin to invite you.
        </div>
      )}
    </div>
  );
}


/* ------------------------------------------------------------------ */
/* Share modal — copy link + WhatsApp/email share buttons             */
/* ------------------------------------------------------------------ */
function ShareModal({ tree, onClose }) {
  const [preview, setPreview] = useState(null);
  const url = typeof window !== "undefined"
    ? `${window.location.origin}/family/${tree.slug || tree.id}`
    : "";
  const shareText = `Explore "${tree.name}" — a family tree on Social Pulse`;

  useEffect(() => {
    http.get(`/family-trees/${tree.id}/share`).then((r) => setPreview(r.data)).catch(() => {});
  }, [tree.id]);

  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch (e) { toast.error("Copy failed — long-press the link to copy"); }
  };
  const openWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(`${shareText}\n${url}`)}`, "_blank");
  };
  const openEmail = () => {
    window.open(`mailto:?subject=${encodeURIComponent(shareText)}&body=${encodeURIComponent(`I thought you'd love this: ${url}`)}`);
  };
  const openTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(url)}`, "_blank");
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#14213D]/25 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="wofy-card w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="ft-share-modal">
        <div className="flex items-start justify-between mb-2">
          <div className="overline">SHARE THIS FAMILY TREE</div>
          <button onClick={onClose} className="opacity-60 hover:opacity-100"><X className="w-5 h-5" /></button>
        </div>
        <h3 className="font-display text-2xl mb-1">{tree.name}</h3>
        <p className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>
          {preview ? `${preview.member_count} member${preview.member_count === 1 ? "" : "s"} · ${preview.country || "Global"}` : "Loading preview…"}
        </p>
        {tree.visibility !== "public" && (
          <p className="mt-2 text-xs p-2 rounded" style={{ background: "var(--wofy-surface)", color: "var(--wofy-text-soft)" }}>
            This tree is <b>{tree.visibility}</b>. Recipients will see only the name and be asked to request access.
          </p>
        )}
        <div className="mt-4 flex items-center gap-2">
          <input readOnly value={url} className="wofy-input flex-1 text-xs" data-testid="ft-share-url" />
          <button onClick={copy} data-testid="ft-share-copy" className="wofy-btn-primary !px-3">
            <Copy className="w-4 h-4" />
          </button>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2">
          <button onClick={openWhatsApp} data-testid="ft-share-whatsapp" className="wofy-btn-ghost text-xs">WhatsApp</button>
          <button onClick={openEmail} data-testid="ft-share-email" className="wofy-btn-ghost text-xs">Email</button>
          <button onClick={openTwitter} data-testid="ft-share-twitter" className="wofy-btn-ghost text-xs">Twitter/X</button>
        </div>
      </div>
    </div>
  );
}


/* ------------------------------------------------------------------ */
/* GEDCOM export / import                                             */
/* ------------------------------------------------------------------ */
function GedcomModal({ tree, isAdmin, onClose, onImported }) {
  const [busy, setBusy] = useState(false);
  const doExport = () => {
    // Force a browser download from the API endpoint with cookies.
    const link = document.createElement("a");
    link.href = `${API}/family-trees/${tree.id}/gedcom`;
    link.download = `${tree.slug || tree.id}.ged`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const doImport = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("GEDCOM file must be < 5 MB");
    const content = await file.text();
    setBusy(true);
    try {
      const { data } = await http.post(`/family-trees/${tree.id}/gedcom/import`, { content });
      toast.success(`Imported ${data.members_added} members, ${data.relationships_added} relationships`);
      onImported?.();
      onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="fixed inset-0 z-50 bg-[#14213D]/25 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="wofy-card w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="ft-gedcom-modal">
        <div className="flex items-start justify-between mb-2">
          <div className="overline">GEDCOM · GENEALOGY EXCHANGE</div>
          <button onClick={onClose} className="opacity-60 hover:opacity-100"><X className="w-5 h-5" /></button>
        </div>
        <h3 className="font-display text-2xl mb-2">Move your tree in and out</h3>
        <p className="text-xs mb-4" style={{ color: "var(--wofy-text-soft)" }}>
          GEDCOM is the free, open standard used by Ancestry, MyHeritage, FamilySearch,
          RootsMagic, and every major genealogy program. Export to move your tree
          anywhere; import to bring an existing family tree into Social Pulse.
        </p>
        <div className="space-y-3">
          <button onClick={doExport} data-testid="ft-gedcom-export" className="wofy-btn-primary w-full justify-center">
            <Download className="w-4 h-4" /> Export {tree.name}
          </button>
          {isAdmin ? (
            <label className={`wofy-btn-ghost w-full justify-center cursor-pointer ${busy ? "opacity-50" : ""}`} data-testid="ft-gedcom-import-label">
              <Upload className="w-4 h-4" /> {busy ? "Importing…" : "Import a .ged file"}
              <input type="file" accept=".ged,text/plain,application/x-gedcom" className="hidden" onChange={doImport} disabled={busy} data-testid="ft-gedcom-import" />
            </label>
          ) : (
            <p className="text-xs text-center italic" style={{ color: "var(--wofy-text-soft)" }}>
              Only tree admins can import.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}


/* ------------------------------------------------------------------ */
/* InviteTab — lets any tree member send an invitation to a relative   */
/* they want to join the tree. Two channels:                          */
/*   1. Shareable invite link (copy / WhatsApp / email deep-link)     */
/*   2. Direct invite form (name + email or phone + personal note)    */
/* ------------------------------------------------------------------ */
function InviteTab({ treeId, treeName, isAdmin, user }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState([]);      // list of accepted invitation payloads
  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const shareUrl = `${window.location.origin}/family/${treeId}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Invite link copied — paste it wherever you'd like.");
    } catch {
      toast.error("Couldn't copy — long-press the link and copy manually.");
    }
  };

  const openWhatsApp = () => {
    const text = encodeURIComponent(
      `${user?.name || "I"} invited you to join our family tree "${treeName}" on Social Pulse: ${shareUrl}`,
    );
    window.open(`https://wa.me/?text=${text}`, "_blank", "noopener");
  };

  const openMail = () => {
    const subject = encodeURIComponent(`You're invited to join "${treeName}" on Social Pulse`);
    const body = encodeURIComponent(
      `Hi${f.name ? " " + f.name : ""},\n\n` +
      `I'd love for you to join our family tree "${treeName}" on Social Pulse.\n\n` +
      `${f.note ? f.note + "\n\n" : ""}` +
      `Open this link to view and join:\n${shareUrl}\n\n— ${user?.name || "A family member"}`,
    );
    const to = f.email ? encodeURIComponent(f.email) : "";
    window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error("Add the person's name so they know it's from you.");
    if (!f.email.trim() && !f.phone.trim()) return toast.error("Add an email or a phone number.");
    setBusy(true);
    try {
      const { data } = await http.post(`/family-trees/${treeId}/invitations`, {
        name: f.name.trim(),
        email: f.email.trim() || null,
        phone: f.phone.trim() || null,
        note: f.note.trim() || null,
      });
      setSent((prev) => [{ ...data, ts: Date.now() }, ...prev].slice(0, 20));
      setF({ name: "", email: "", phone: "", note: "" });
      toast.success(`Invitation queued for ${data.name || f.name}.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't queue the invitation.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="invite-tab">
      {/* Shareable invite link */}
      <div className="wofy-card">
        <div className="overline mb-2">SHAREABLE INVITE LINK</div>
        <p className="text-sm mb-3" style={{ color: "var(--wofy-text-soft)" }}>
          Anyone with this link lands on the tree's public card and can request access — perfect for family WhatsApp groups.
        </p>
        <div className="flex flex-col md:flex-row gap-2 mb-3">
          <input
            readOnly
            value={shareUrl}
            className="wofy-input flex-1"
            onFocus={(e) => e.target.select()}
            data-testid="invite-share-url"
          />
          <div className="flex gap-2">
            <button onClick={copyLink} className="wofy-btn-primary" data-testid="invite-copy-link">
              <Copy className="w-4 h-4" /> Copy
            </button>
            <button onClick={openWhatsApp} className="wofy-btn-ghost" data-testid="invite-whatsapp">
              WhatsApp
            </button>
            <button onClick={openMail} className="wofy-btn-ghost" data-testid="invite-email">
              Email
            </button>
          </div>
        </div>
      </div>

      {/* Direct invite form */}
      <form onSubmit={submit} className="wofy-card space-y-3" data-testid="invite-form">
        <div>
          <div className="overline">INVITE SOMEONE DIRECTLY</div>
          <p className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>
            We'll log this in the family invitations list so admins can follow up.
          </p>
        </div>
        <label className="block">
          <div className="text-xs font-mono uppercase tracking-wider mb-1" style={{ color: "var(--wofy-text-soft)" }}>Name *</div>
          <input required value={f.name} onChange={(e) => upd("name", e.target.value)} className="wofy-input" placeholder="e.g. Cousin Anita" data-testid="invite-name" />
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="block">
            <div className="text-xs font-mono uppercase tracking-wider mb-1" style={{ color: "var(--wofy-text-soft)" }}>Email</div>
            <input type="email" value={f.email} onChange={(e) => upd("email", e.target.value)} className="wofy-input" placeholder="anita@example.com" data-testid="invite-email-input" />
          </label>
          <label className="block">
            <div className="text-xs font-mono uppercase tracking-wider mb-1" style={{ color: "var(--wofy-text-soft)" }}>Phone</div>
            <input type="tel" value={f.phone} onChange={(e) => upd("phone", e.target.value)} className="wofy-input" placeholder="+91 98…" data-testid="invite-phone-input" />
          </label>
        </div>
        <label className="block">
          <div className="text-xs font-mono uppercase tracking-wider mb-1" style={{ color: "var(--wofy-text-soft)" }}>Personal note (optional)</div>
          <textarea rows={3} value={f.note} onChange={(e) => upd("note", e.target.value)} className="wofy-input" placeholder="Hi Anita — Uncle Saji here. Would love to have you on our family tree…" maxLength={800} data-testid="invite-note" />
        </label>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={openMail} className="wofy-btn-ghost" data-testid="invite-mail-compose">
            Compose in email
          </button>
          <button type="submit" disabled={busy} className="wofy-btn-primary disabled:opacity-60" data-testid="invite-submit">
            {busy ? "Sending…" : "Send invitation"}
          </button>
        </div>
      </form>

      {sent.length > 0 && (
        <div className="wofy-card">
          <div className="overline mb-2">RECENTLY SENT (THIS SESSION)</div>
          <ul className="space-y-2 text-sm">
            {sent.map((s) => (
              <li key={s.ts} className="flex items-center justify-between border-b pb-1" style={{ borderColor: "var(--wofy-border)" }} data-testid={`invite-sent-${s.ts}`}>
                <span><b>{s.name}</b> · {s.email || s.phone || "no contact"}</span>
                <span className="text-[10px] font-mono uppercase opacity-60">Queued</span>
              </li>
            ))}
          </ul>
          <p className="text-[11px] mt-2 opacity-70">
            Admins see the full list in the Family Management Center.
          </p>
        </div>
      )}
    </div>
  );
}





/* ------------------------------------------------------------------ */
/* Request Access landing — shown to non-members visiting an invite    */
/* tree. Displays the tree's public card + a structured form so tree  */
/* admins can vet the requester before granting access.                */
/* ------------------------------------------------------------------ */
function RequestAccessLanding({ card, user }) {
  const [tab, setTab] = useState("request");
  return (
    <div className="max-w-4xl mx-auto px-6 py-8" data-testid="ra-landing">
      <Link to="/family" className="text-xs underline" style={{ color: "var(--wofy-primary)" }}>← Back to family trees</Link>

      {/* Tree card hero */}
      <div className="mt-3 rounded-2xl overflow-hidden border" style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }} data-testid="ra-tree-card">
        {card.cover_image ? (
          <div className="h-40 md:h-56 w-full bg-cover bg-center" style={{ backgroundImage: `url(${card.cover_image})` }} />
        ) : (
          <div className="h-32 md:h-40 w-full flex items-center justify-center" style={{ background: "linear-gradient(120deg,#f3e8ff 0%,#fce7f3 40%,#dbeafe 100%)" }}>
            <Users className="w-10 h-10" style={{ color: "var(--wofy-primary)" }} />
          </div>
        )}
        <div className="p-5">
          <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>{(card.visibility || "").toUpperCase()} · REQUEST ACCESS</div>
          <h1 className="font-display text-3xl md:text-4xl leading-tight" data-testid="ra-tree-name">{card.name}</h1>
          <div className="mt-2 text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
            {card.country || "Global"} · {card.member_count} member{card.member_count === 1 ? "" : "s"}
            {card.owner_name && <> · Owner: {card.owner_name}</>}
          </div>
          {card.description && (
            <p className="mt-3 text-sm max-w-2xl" style={{ color: "var(--wofy-text-soft)" }}>{card.description}</p>
          )}
        </div>
      </div>

      {/* Tabs — currently one, but the pattern scales when we add more   */}
      {/* (e.g. Public Members preview for public trees, About, etc.).    */}
      <div className="flex gap-2 mt-6 mb-4 border-b overflow-x-auto" style={{ borderColor: "var(--wofy-line)" }}>
        {[
          { v: "request", label: "Request Access", icon: UserPlus },
        ].map(({ v, label, icon: Icon }) => (
          <button
            key={v}
            data-testid={`ra-tab-${v}`}
            onClick={() => setTab(v)}
            className="px-3 py-2 text-sm border-b-2 -mb-px inline-flex items-center gap-1.5 whitespace-nowrap"
            style={{ borderColor: tab === v ? "var(--wofy-primary)" : "transparent", color: tab === v ? "var(--wofy-primary)" : "var(--wofy-text-soft)" }}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "request" && (
        user ? (
          <RequestAccessForm treeId={card.id} treeName={card.name} user={user} />
        ) : (
          <div className="rounded-lg border p-6 text-center" style={{ borderColor: "var(--wofy-border)" }} data-testid="ra-signin-gate">
            <p className="text-sm mb-3">Please sign in first so the family admin knows who's asking.</p>
            <Link to="/login" className="wofy-btn-primary inline-flex">Sign in to continue</Link>
          </div>
        )
      )}
    </div>
  );
}

function RequestAccessForm({ treeId, treeName, user }) {
  // Auto-populate location from LocalityContext (browser geoloc / IP fallback
  // already ran globally at app boot, so this is usually pre-filled).
  const { locality } = useLocality();
  const autoLoc = locality
    ? [locality.city, locality.state, locality.country].filter(Boolean).join(", ")
    : "";

  const [f, setF] = useState({
    name: user?.name || "",
    phone: user?.phone_number || "",
    email: user?.email || "",
    location: autoLoc,
    reason: "",
    comments: "",
  });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));

  // Re-populate location once the locality context resolves (async on first
  // page load).
  useEffect(() => {
    if (!f.location && autoLoc) upd("location", autoLoc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoc]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error("Please tell them your name");
    if (!f.reason.trim()) return toast.error("Please share your reason for requesting access");
    setBusy(true);
    try {
      await http.post(`/family-trees/${treeId}/access-request`, {
        name: f.name.trim(),
        phone: f.phone.trim() || null,
        email: f.email.trim() || null,
        location: f.location.trim() || null,
        reason: f.reason.trim(),
        comments: f.comments.trim() || null,
        // Legacy `message` mirror so older admin dashboards keep working.
        message: f.reason.trim() + (f.comments.trim() ? `\n\n${f.comments.trim()}` : ""),
      });
      setSent(true);
      toast.success(`Request sent to the ${treeName} admins — they'll review it soon.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit request");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="rounded-lg border p-6" style={{ borderColor: "var(--wofy-border)", background: "rgba(16,185,129,0.05)" }} data-testid="ra-sent">
        <div className="overline mb-1" style={{ color: "#059669" }}>✓ REQUEST SUBMITTED</div>
        <h3 className="font-display text-2xl">Thanks — your request is on its way.</h3>
        <p className="text-sm mt-2" style={{ color: "var(--wofy-text-soft)" }}>
          An admin of <b>{treeName}</b> will review your details and get back to you. If your request is denied, you can re-apply after 7 days.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-lg border p-5 md:p-6 space-y-4" style={{ borderColor: "var(--wofy-border)" }} data-testid="ra-form">
      <div>
        <div className="overline mb-1">TELL THEM WHO YOU ARE</div>
        <p className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>
          The family admin will see this. Only share what you're comfortable with.
        </p>
      </div>

      <label className="block">
        <div className="text-xs font-mono uppercase tracking-wider mb-1" style={{ color: "var(--wofy-text-soft)" }}>Your name *</div>
        <input
          required
          value={f.name}
          onChange={(e) => upd("name", e.target.value)}
          className="wofy-input"
          placeholder="e.g. Anita Kurisummoottil"
          data-testid="ra-name"
        />
      </label>

      <label className="block">
        <div className="text-xs font-mono uppercase tracking-wider mb-1 flex items-center gap-2" style={{ color: "var(--wofy-text-soft)" }}>
          <span>Location</span>
          {autoLoc && <span className="normal-case tracking-normal opacity-70 text-[10px]">(auto-detected — edit if needed)</span>}
        </div>
        <input
          value={f.location}
          onChange={(e) => upd("location", e.target.value)}
          className="wofy-input"
          placeholder="City, State, Country"
          data-testid="ra-location"
        />
      </label>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="block">
          <div className="text-xs font-mono uppercase tracking-wider mb-1" style={{ color: "var(--wofy-text-soft)" }}>Phone number</div>
          <input
            type="tel"
            value={f.phone}
            onChange={(e) => upd("phone", e.target.value)}
            className="wofy-input"
            placeholder="+91 98…"
            data-testid="ra-phone"
          />
        </label>
        <label className="block">
          <div className="text-xs font-mono uppercase tracking-wider mb-1" style={{ color: "var(--wofy-text-soft)" }}>Email</div>
          <input
            type="email"
            value={f.email}
            onChange={(e) => upd("email", e.target.value)}
            className="wofy-input"
            placeholder="you@example.com"
            data-testid="ra-email"
          />
        </label>
      </div>

      <label className="block">
        <div className="text-xs font-mono uppercase tracking-wider mb-1" style={{ color: "var(--wofy-text-soft)" }}>Your reason to request access *</div>
        <input
          required
          value={f.reason}
          onChange={(e) => upd("reason", e.target.value)}
          className="wofy-input"
          placeholder="e.g. I'm Saji's cousin from Kochi — I'd like to view family history"
          maxLength={200}
          data-testid="ra-reason"
        />
      </label>

      <label className="block">
        <div className="text-xs font-mono uppercase tracking-wider mb-1" style={{ color: "var(--wofy-text-soft)" }}>Comments</div>
        <textarea
          rows={4}
          value={f.comments}
          onChange={(e) => upd("comments", e.target.value)}
          className="wofy-input"
          placeholder="Anything else the admin should know (relationship, family branch, mutual relatives, etc.)"
          maxLength={1000}
          data-testid="ra-comments"
        />
      </label>

      <div className="flex justify-end gap-3 pt-2">
        <button
          type="submit"
          disabled={busy}
          className="wofy-btn-primary disabled:opacity-60"
          data-testid="ra-submit"
        >
          {busy ? "Sending…" : "Send request"}
        </button>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------ */
/* Family Timeline — every birth/death/wedding/event on one horizontal
/* ribbon. Auto-generated from members + relationships + events.      */
/* ------------------------------------------------------------------ */
function FamilyTimeline({ members, rels, events }) {
  const items = useMemo(() => {
    const out = [];
    const nameOf = (id) => {
      const m = members.find((x) => x.id === id);
      return m ? [m.first_name, m.last_name].filter(Boolean).join(" ") : "Unknown";
    };
    const yearOf = (dateStr, yearOnly) => {
      if (!dateStr) return null;
      const s = String(dateStr);
      if (yearOnly || s.length === 4) {
        const y = parseInt(s.slice(0, 4), 10);
        return Number.isFinite(y) ? y : null;
      }
      const y = parseInt(s.slice(0, 4), 10);
      return Number.isFinite(y) ? y : null;
    };

    for (const m of members) {
      const b = yearOf(m.birth_date, m.birth_date_is_year_only);
      const d = yearOf(m.death_date, m.death_date_is_year_only);
      const nm = [m.first_name, m.last_name].filter(Boolean).join(" ");
      if (b) out.push({
        year: b, exactDate: m.birth_date, type: "birth", icon: "\u{1F382}",
        title: `${nm} was born`, subtitle: m.birth_place || "", color: "#2C7A4B",
      });
      if (d) out.push({
        year: d, exactDate: m.death_date, type: "death", icon: "\u{1F54A}",
        title: `${nm} passed away`, subtitle: m.death_place || "", color: "#5B4A73",
        memorialSlug: m.memorial_slug,
      });
    }
    for (const r of rels) {
      if (r.type !== "spouse") continue;
      const wd = r.wedding_date || null;
      const y = yearOf(wd);
      if (!y) continue;
      out.push({
        year: y, exactDate: wd, type: "wedding", icon: "\u{1F48D}",
        title: `${nameOf(r.person1_id)} & ${nameOf(r.person2_id)} married`,
        subtitle: "", color: "#B25D38",
      });
    }
    for (const e of events) {
      const y = yearOf(e.event_date);
      if (!y) continue;
      const iconMap = { birthday: "\u{1F382}", wedding: "\u{1F48D}", funeral: "\u{1F54A}", reunion: "\u{1F389}" };
      out.push({
        year: y, exactDate: e.event_date, type: e.event_type || "event",
        icon: iconMap[e.event_type] || "\u{1F4C5}",
        title: e.title, subtitle: e.location || "", color: "var(--wofy-primary)",
      });
    }
    return out.sort((a, b) => a.year - b.year);
  }, [members, rels, events]);

  if (items.length === 0) {
    return (
      <div className="wofy-card text-center py-16" data-testid="ft-timeline-empty">
        <Clock className="w-6 h-6 mx-auto mb-2" style={{ color: "var(--wofy-primary)" }} />
        <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>
          The timeline fills in automatically as you add members, weddings and family events. Add a birth date to get started.
        </p>
      </div>
    );
  }

  const byDecade = new Map();
  for (const it of items) {
    const dec = Math.floor(it.year / 10) * 10;
    if (!byDecade.has(dec)) byDecade.set(dec, []);
    byDecade.get(dec).push(it);
  }
  const decades = [...byDecade.keys()].sort((a, b) => a - b);

  return (
    <div data-testid="ft-timeline-tab" className="overflow-x-auto">
      <div className="min-w-max pb-6">
        <div className="flex gap-8 relative">
          <div className="absolute left-0 right-0 top-6 h-0.5" style={{ background: "var(--wofy-line)" }} />
          {decades.map((dec) => (
            <div key={dec} className="relative flex flex-col items-start min-w-[220px] pt-2">
              <div className="flex items-center gap-2 mb-4 relative z-10">
                <div className="w-3 h-3 rounded-full" style={{ background: "var(--wofy-primary)" }} />
                <div className="font-mono text-sm font-bold tracking-wider" style={{ color: "var(--wofy-primary)" }}>
                  {dec}s
                </div>
              </div>
              <div className="space-y-3 pl-5">
                {byDecade.get(dec).map((it, idx) => (
                  <div
                    key={`${dec}-${idx}`}
                    data-testid={`ft-timeline-item-${it.year}-${it.type}`}
                    className="rounded-xl border p-3 text-xs"
                    style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-paper)", borderLeftWidth: 3, borderLeftColor: it.color }}
                  >
                    <div className="flex items-baseline gap-2">
                      <span className="text-lg leading-none">{it.icon}</span>
                      <span className="font-mono font-bold" style={{ color: it.color }}>{it.year}</span>
                    </div>
                    <div className="mt-1 font-semibold">{it.title}</div>
                    {it.subtitle && <div className="text-[10px]" style={{ color: "var(--wofy-text-soft)" }}>{it.subtitle}</div>}
                    {it.memorialSlug && (
                      <Link to={`/celebrate/${it.memorialSlug}`} className="mt-1 inline-flex items-center gap-0.5 text-[10px] underline" style={{ color: "#5B4A73" }}>
                        <Flower2 className="w-3 h-3" /> Memorial
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <p className="text-[11px] italic text-center mt-2" style={{ color: "var(--wofy-text-soft)" }}>
        Scroll horizontally to travel through your family's history →
      </p>
    </div>
  );
}


function AccessTab({ treeId }) {
  const [reqs, setReqs] = useState(null);
  const [members, setMembers] = useState([]);
  const [claims, setClaims] = useState([]);
  const [pendingStories, setPendingStories] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await http.get(`/family-trees/${treeId}/access-requests`);
      setReqs(data.items || []);
    } catch (e) {
      toast.error("Failed to load requests");
      setReqs([]);
    }
    try {
      const { data } = await http.get(`/family-trees/${treeId}/members-list`);
      setMembers(data.items || []);
    } catch { /* endpoint may not exist yet — soft-fail */ }
    try {
      const { data } = await http.get(`/family-trees/${treeId}/claims`, { params: { status: "pending" } });
      setClaims(data.claims || []);
    } catch { setClaims([]); }
    // Aggregate pending stories from every member in this tree.
    try {
      const { data } = await http.get(`/family-trees/${treeId}/members`);
      const items = [];
      for (const m of (data.items || [])) {
        for (const s of (m.stories || [])) {
          if (s.status === "pending") items.push({ ...s, member_id: m.id, member_name: [m.first_name, m.last_name].filter(Boolean).join(" ") });
        }
      }
      items.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      setPendingStories(items);
    } catch { setPendingStories([]); }
  }, [treeId]);

  useEffect(() => { load(); }, [load]);

  const decide = async (rid, action) => {
    try {
      await http.post(`/family-trees/${treeId}/access-requests/${rid}/${action}`, { note: null });
      toast.success(`Request ${action}d`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const decideClaim = async (cid, action) => {
    try {
      await http.post(`/family-trees/${treeId}/claims/${cid}/${action}`);
      toast.success(`Claim ${action === "approve" ? "approved" : "denied"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const revoke = async (userId) => {
    if (!window.confirm("Revoke this member's access?")) return;
    try {
      await http.post(`/family-trees/${treeId}/access/${userId}/revoke`);
      toast.success("Access revoked");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  if (reqs === null) return <p className="text-sm mt-6" style={{ color: "var(--wofy-text-soft)" }}>Loading…</p>;

  const pending = reqs.filter((r) => r.status === "pending");
  const history = reqs.filter((r) => r.status !== "pending").slice(0, 15);

  const decideStory = async (memberId, storyId, action) => {
    try {
      await http.post(`/family-trees/${treeId}/members/${memberId}/stories/${action}`, { story_id: storyId });
      toast.success(action === "approve" ? "Story approved" : "Story deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="mt-6 space-y-8" data-testid="access-tab">
      <InviteManagerPanel treeId={treeId} />
      <TreeCorrectionsPanel treeId={treeId} />
      <section>
        <div className="overline mb-3">PENDING STORIES · {pendingStories.length}</div>
        {pendingStories.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>No family stories pending your review.</p>
        ) : (
          <ul className="space-y-3">
            {pendingStories.map((s) => (
              <li key={s.id} data-testid={`pending-story-${s.id}`} className="rounded-lg border p-3" style={{ borderColor: "var(--wofy-border)" }}>
                <div className="flex flex-wrap items-baseline gap-2 mb-1 text-sm">
                  <b>{s.title || "Untitled story"}</b>
                  {s.kind && <span className="text-[10px] font-mono uppercase opacity-60">{s.kind}</span>}
                  <span className="text-xs opacity-70">on <i>{s.member_name || "member"}</i></span>
                </div>
                <div className="text-[11px] font-mono opacity-60 mb-2">
                  by {s.contributor_name || "Someone"} · {new Date(s.created_at).toLocaleDateString()}
                </div>
                <div className="text-sm p-2 rounded" dir="auto" style={{ background: "var(--wofy-surface)", whiteSpace: "pre-wrap" }}>
                  {s.body}
                </div>
                <div className="flex gap-2 mt-2 justify-end">
                  <button onClick={() => decideStory(s.member_id, s.id, "approve")} className="wofy-btn-primary text-xs" data-testid={`story-approve-${s.id}`}>Approve</button>
                  <button onClick={() => decideStory(s.member_id, s.id, "delete")} className="wofy-btn-ghost text-xs" style={{ color: "#B23A3A" }} data-testid={`story-delete-${s.id}`}>Delete</button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <div className="overline mb-3">MEMBER CLAIMS · PENDING · {claims.length}</div>
        {claims.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>No pending member claims.</p>
        ) : (
          <ul className="space-y-3">
            {claims.map((c) => (
              <li key={c.id} data-testid={`claim-row-${c.id}`} className="flex items-start gap-3 rounded-lg border p-3" style={{ borderColor: "var(--wofy-border)" }}>
                {c.user_avatar ? (
                  <img src={c.user_avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full flex items-center justify-center font-display" style={{ background: "var(--wofy-surface)" }}>
                    {(c.user_name || "?").trim().slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm">
                    <b>{c.user_name || "Someone"}</b>
                    <span className="opacity-70"> wants to edit </span>
                    <b>{c.member_name}</b>
                  </div>
                  <div className="text-[11px] font-mono opacity-60">
                    {c.relationship && <span>Relation: {c.relationship} · </span>}
                    {new Date(c.created_at).toLocaleDateString()}
                  </div>
                  {c.note && <p className="mt-2 text-sm italic">"{c.note}"</p>}
                </div>
                <div className="flex flex-col gap-1.5">
                  <button onClick={() => decideClaim(c.id, "approve")} className="wofy-btn-primary text-xs" data-testid={`claim-approve-${c.id}`}>
                    Approve
                  </button>
                  <button onClick={() => decideClaim(c.id, "deny")} className="wofy-btn-ghost text-xs" style={{ color: "#B23A3A" }} data-testid={`claim-deny-${c.id}`}>
                    Deny
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <div className="overline mb-3">PENDING REQUESTS · {pending.length}</div>
        {pending.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>No pending access requests.</p>
        ) : (
          <ul className="space-y-3">
            {pending.map((r) => (
              <li key={r.id} data-testid={`access-req-${r.id}`} className="flex items-start gap-3 rounded-lg border p-3" style={{ borderColor: "var(--wofy-border)" }}>
                {r.user_avatar ? (
                  <img src={r.user_avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full flex items-center justify-center font-display" style={{ background: "var(--wofy-surface)" }}>
                    {(r.user_name || "?").trim().slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">
                    {r.user_handle ? (
                      <Link to={`/u/${r.user_handle}`} className="hover:underline">{r.user_name || "Unknown"}</Link>
                    ) : (r.user_name || "Unknown")}
                  </div>
                  <div className="text-[11px] font-mono" style={{ color: "var(--wofy-text-soft)" }}>
                    @{r.user_handle || "user"} · {new Date(r.created_at).toLocaleDateString()}
                  </div>
                  {r.message && <p className="mt-2 text-sm italic">"{r.message}"</p>}
                  {/* Structured requester profile — new form fields */}
                  {(r.requester_name || r.requester_phone || r.requester_email || r.requester_location || r.reason || r.comments) && (
                    <div className="mt-2 rounded-md border p-2 text-xs space-y-0.5" style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}>
                      {r.requester_name && <div><span className="font-mono opacity-60 uppercase text-[10px] mr-1">Name</span>{r.requester_name}</div>}
                      {r.requester_location && <div><span className="font-mono opacity-60 uppercase text-[10px] mr-1">Location</span>{r.requester_location}</div>}
                      {r.requester_phone && <div><span className="font-mono opacity-60 uppercase text-[10px] mr-1">Phone</span><a href={`tel:${r.requester_phone}`} className="underline">{r.requester_phone}</a></div>}
                      {r.requester_email && <div><span className="font-mono opacity-60 uppercase text-[10px] mr-1">Email</span><a href={`mailto:${r.requester_email}`} className="underline">{r.requester_email}</a></div>}
                      {r.reason && <div><span className="font-mono opacity-60 uppercase text-[10px] mr-1">Reason</span>{r.reason}</div>}
                      {r.comments && <div className="mt-1 pt-1 border-t italic" style={{ borderColor: "var(--wofy-border)" }}>"{r.comments}"</div>}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1.5">
                  <button
                    onClick={() => decide(r.id, "approve")}
                    data-testid={`access-approve-${r.id}`}
                    className="wofy-btn-primary text-xs"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => decide(r.id, "deny")}
                    data-testid={`access-deny-${r.id}`}
                    className="wofy-btn-ghost text-xs"
                    style={{ color: "#B23A3A" }}
                  >
                    Deny
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {members.length > 0 && (
        <section>
          <div className="overline mb-3">CURRENT MEMBERS · {members.length}</div>
          <ul className="space-y-2">
            {members.map((m) => (
              <li key={m.user_id} className="flex items-center gap-3 rounded-lg border p-2" style={{ borderColor: "var(--wofy-border)" }}>
                <div className="flex-1 text-sm">
                  <b>{m.user_name || m.user_id}</b>
                  <span className="ml-2 text-[11px] uppercase font-mono" style={{ color: "var(--wofy-text-soft)" }}>{m.role}</span>
                </div>
                {m.role !== "owner" && (
                  <button
                    onClick={() => revoke(m.user_id)}
                    className="wofy-btn-ghost text-xs"
                    style={{ color: "#B23A3A" }}
                    data-testid={`access-revoke-${m.user_id}`}
                  >
                    Revoke
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {history.length > 0 && (
        <section>
          <div className="overline mb-3">RECENT DECISIONS</div>
          <ul className="text-xs space-y-1" style={{ color: "var(--wofy-text-soft)" }}>
            {history.map((r) => (
              <li key={r.id}>
                {r.user_name || "Someone"} · <b>{r.status}</b> · {new Date(r.reviewed_at || r.created_at).toLocaleDateString()}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}



/* ------------------------------------------------------------------ */
/* Documents tab — read-only listing of tree PDFs (books, memoirs)    */
/* with an "Open book" link into the immersive FamilyBookViewer.      */
/* Uploads / deletes happen in Family Management Center → Documents.  */
/* ------------------------------------------------------------------ */
function ArchiveTab({ treeId, treeSlug, isAdmin, user, members = [] }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [caption, setCaption] = useState("");
  const [tagIds, setTagIds] = useState([]);
  const [editTagsFor, setEditTagsFor] = useState(null);
  const toggleTag = (id) => setTagIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const toggleCardTag = async (f, memberId) => {
    const current = (f.member_tags || []).map((t) => t.id);
    const next = current.includes(memberId) ? current.filter((x) => x !== memberId) : [...current, memberId];
    try {
      const { data } = await http.patch(`/family-trees/${treeId}/archive-files/${f.id}`, { member_ids: next });
      setItems((prev) => prev.map((x) => (x.id === f.id ? { ...x, member_tags: data.member_tags } : x)));
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't update tags"); }
  };
  const load = async () => {
    try {
      const { data } = await http.get(`/family-trees/${treeId}/archive-files`);
      setItems(data.items || []);
    } catch { /* ignore */ }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [treeId]);

  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("File must be under 15 MB"); e.target.value = ""; return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", caption);
      fd.append("member_ids", tagIds.join(","));
      await http.post(`/family-trees/${treeId}/archive-files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Added to the family archive");
      setCaption("");
      setTagIds([]);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const del = async (fid) => {
    if (!window.confirm("Remove this file from the archive?")) return;
    try { await http.delete(`/family-trees/${treeId}/archive-files/${fid}`); toast.success("Removed"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="ft-archive-tab">
      <div className="wofy-card mb-4">
        <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>FAMILY ARCHIVE</div>
        <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>
          A shared home for family photos, letters, certificates and documents — preserved for future generations.
          Images, PDF, Word, Excel and text files up to 15 MB.
        </p>
        {user ? (
          <>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <input
                value={caption} onChange={(e) => setCaption(e.target.value)}
                placeholder="Caption (optional)" className="wofy-input max-w-xs"
                data-testid="ft-archive-caption"
              />
              <label className={`wofy-btn-primary cursor-pointer ${busy ? "opacity-60 pointer-events-none" : ""}`} data-testid="ft-archive-upload">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {busy ? "Uploading…" : "Upload a file"}
                <input type="file" className="hidden" accept="image/*,.pdf,.doc,.docx,.txt,.csv,.xls,.xlsx,.ppt,.pptx" onChange={onFile} />
              </label>
            </div>
            {members.length > 0 && (
              <div className="mt-3">
                <div className="text-[10px] font-mono uppercase tracking-wider mb-1.5 opacity-60">
                  Tag people (optional) — tagged files appear on their profiles
                </div>
                <div className="flex gap-1.5 flex-wrap max-h-24 overflow-y-auto" data-testid="ft-archive-tag-picker">
                  {members.map((m) => (
                    <button
                      key={m.id} type="button" onClick={() => toggleTag(m.id)}
                      className="rounded-full px-2.5 py-1 text-[11px] border transition"
                      style={tagIds.includes(m.id)
                        ? { background: "var(--wofy-primary)", color: "#fff", borderColor: "var(--wofy-primary)" }
                        : { borderColor: "var(--wofy-border)", color: "var(--wofy-text-soft)" }}
                      data-testid={`ft-archive-tag-${m.id}`}
                    >
                      {m.first_name} {m.last_name || ""}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <p className="mt-3 text-xs opacity-70">Sign in to add photos or documents.</p>
        )}
      </div>

      {loading ? (
        <div className="wofy-card text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
      ) : items.length === 0 ? (
        <div className="wofy-card text-sm text-center" style={{ color: "var(--wofy-text-soft)" }} data-testid="ft-archive-empty">
          Nothing archived yet — old photographs, wedding invitations, land records and letters all belong here.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((f) => (
            <div key={f.id} className="wofy-card" data-testid={`ft-archive-item-${f.id}`}>
              {f.kind === "photo" ? (
                <a href={resolveImage(f.url)} target="_blank" rel="noreferrer">
                  <img src={resolveImage(f.url)} alt={f.caption || f.filename} className="w-full h-40 object-cover rounded-lg mb-3" loading="lazy" />
                </a>
              ) : (
                <a href={resolveImage(f.url)} target="_blank" rel="noreferrer" className="w-full h-40 rounded-lg mb-3 flex flex-col items-center justify-center gap-2" style={{ background: "var(--wofy-surface)" }}>
                  <FileText className="w-10 h-10" style={{ color: "var(--wofy-primary)" }} />
                  <span className="text-[10px] font-mono uppercase opacity-60">{(f.filename || "").split(".").pop()}</span>
                </a>
              )}
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{f.caption || f.filename}</div>
                  <div className="text-[10px] font-mono opacity-60 mt-1">
                    {f.uploader_name} · {(f.created_at || "").slice(0, 10)} · {Math.max(1, Math.round((f.size_bytes || 0) / 1024))} KB
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <a href={resolveImage(f.url)} target="_blank" rel="noreferrer" className="opacity-60 hover:opacity-100" title="Open / download" data-testid={`ft-archive-download-${f.id}`}>
                    <Download className="w-4 h-4" />
                  </a>
                  {user && members.length > 0 && (
                    <button
                      onClick={() => setEditTagsFor(editTagsFor === f.id ? null : f.id)}
                      className="opacity-50 hover:opacity-100" title="Tag people"
                      data-testid={`ft-archive-edit-tags-${f.id}`}
                    >
                      <UserPlus className="w-4 h-4" />
                    </button>
                  )}
                  {(isAdmin || f.uploader_id === user?.id) && (
                    <button onClick={() => del(f.id)} className="opacity-50 hover:opacity-100" title="Remove" data-testid={`ft-archive-remove-${f.id}`}>
                      <Trash2 className="w-4 h-4" style={{ color: "#B23A3A" }} />
                    </button>
                  )}
                </div>
              </div>
              {editTagsFor === f.id && (
                <div className="mt-2 flex gap-1 flex-wrap max-h-20 overflow-y-auto rounded-lg border p-2" style={{ borderColor: "var(--wofy-border)" }} data-testid={`ft-archive-tag-editor-${f.id}`}>
                  {members.map((m) => {
                    const on = (f.member_tags || []).some((t) => t.id === m.id);
                    return (
                      <button
                        key={m.id} type="button" onClick={() => toggleCardTag(f, m.id)}
                        className="rounded-full px-2 py-0.5 text-[10px] border transition"
                        style={on
                          ? { background: "var(--wofy-primary)", color: "#fff", borderColor: "var(--wofy-primary)" }
                          : { borderColor: "var(--wofy-border)", color: "var(--wofy-text-soft)" }}
                        data-testid={`ft-archive-tag-editor-${f.id}-${m.id}`}
                      >
                        {m.first_name} {m.last_name || ""}
                      </button>
                    );
                  })}
                </div>
              )}
              {(f.member_tags || []).length > 0 && (
                <div className="mt-2 flex gap-1 flex-wrap">
                  {f.member_tags.map((t) => (
                    <Link key={t.id} to={`/family/${treeSlug}/member/${t.id}`}
                      className="rounded-full px-2 py-0.5 text-[10px] border hover:bg-black/5 transition"
                      style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text-soft)" }}
                      data-testid={`ft-archive-item-tag-${t.id}`}
                    >
                      {t.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DocumentsList({ treeSlug, isAdmin }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await http.get(`/family-trees/${treeSlug}/documents`);
        if (!cancelled) setItems(data.items || []);
      } catch { /* ignore */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [treeSlug]);

  if (loading) {
    return <div className="wofy-card text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  }
  if (items.length === 0) {
    return (
      <div className="wofy-card text-sm text-center" style={{ color: "var(--wofy-text-soft)" }} data-testid="ft-documents-empty">
        No documents yet. {isAdmin
          ? <Link className="underline" to={`/family/${treeSlug}/manage?tab=documents`}>Upload the family book →</Link>
          : "Ask an admin to upload the family book."}
      </div>
    );
  }
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="ft-documents-grid">
      {items.map((d) => (
        <Link
          key={d.id}
          to={`/family/${treeSlug}/book/${d.id}`}
          className="wofy-card overflow-hidden hover:shadow-lg transition"
          data-testid={`ft-document-${d.id}`}
        >
          <div className="aspect-[3/4] bg-gradient-to-br from-[#2d1e17] via-[#3a2a1e] to-[#1f1611] flex items-center justify-center overflow-hidden">
            <BookOpen className="w-20 h-20 opacity-70 text-amber-100" />
          </div>
          <div className="mt-3">
            <div className="font-semibold">{d.title}</div>
            {d.description && (
              <div className="text-xs mt-1 opacity-70 line-clamp-2">{d.description}</div>
            )}
            <div className="text-[10px] font-mono uppercase mt-2 opacity-60">
              {d.status === "ready"
                ? <>{d.page_count} pages · Ready</>
                : d.status === "processing"
                  ? <>Processing… {d.page_count || 0} of ~{d.page_count || "?"} pages</>
                  : d.status}
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}

